import { client } from "../api/client.js";
import { setPageContext } from "../utils/pageContext.js";
import { formatDateMDY } from "../utils/date.js";
import { renderPageHeader } from "../ui/pageHeader.js";
import { toast } from '../ui/toast.js';
import { confirmModal } from '../ui/confirmModal.js';
import { promptModal } from '../ui/promptModal.js';
import { escapeHtml } from '../ui/escapeHtml.js';
import { openMediaLibrary } from './create-social.js';
import { openDocPicker } from '../ui/docPicker.js';
import { computeBrainScore } from '../utils/brainScore.js';

// SVG Icon helper (Phase 32A)
const _ICONS = {
  'file-text': '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>',
  'file': '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>',
  'package': '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>',
  'folder': '<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>',
  'archive': '<path d="M21 8v13H3V8"/><path d="M23 3H1v5h22V3z"/><line x1="10" y1="12" x2="14" y2="12"/>',
  'link': '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  'paperclip': '<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>',
  'pencil': '<path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>',
  'check': '<polyline points="20 6 9 17 4 12"/>',
  'trash': '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
  'target': '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>',
  'users': '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  'megaphone': '<path d="m3 11 18-5v12L3 14v-3z"/>',
  'message': '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  'briefcase': '<rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
  'hash': '<line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/>',
  'x-circle': '<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>',
  'send': '<line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>',
};

function svgIcon(name, w = 16, h = 16) {
  return `<svg width="${w}" height="${h}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${_ICONS[name] || ''}</svg>`;
}

// Helper: escape + convert newlines to <br> for display
const escapeHTML = (str) => {
  if (!str) return "";
  return escapeHtml(str).replace(/\n/g, "<br>");
};

// Lightweight markdown renderer for AI chat messages
function renderChatMarkdown(text) {
  if (!text) return '';
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^### (.+)$/gm, '<h4 class="chat-h4">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="chat-h3">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="chat-h2">$1</h2>')
    .replace(/^- (.+)$/gm, '<li class="chat-li">$1</li>')
    .replace(/(<li class="chat-li">[\s\S]*?<\/li>\n?)+/g, '<ul class="chat-ul">$&</ul>')
    .replace(/^\d+\. (.+)$/gm, '<li class="chat-li chat-li--ordered">$1</li>')
    .replace(/`([^`]+)`/g, '<code class="chat-code">$1</code>')
    .replace(/\n/g, '<br>');
}

export class MemoryPage {
  constructor() {
    this.memoryData = null;
    this.personas = [];
    // Phase 7 — also honor ?tab=chat&prompt=... deep-links from empty
    // states (calendar/library/campaigns "Talk to Heist" CTAs). Parse
    // once at construction and pass to getInitialTab + stash the prompt.
    let _deepLinkThread = null;
    let _deepLinkTab = null;
    let _deepLinkPrompt = null;
    let _deepLinkNew = false;
    try {
        const hash = (location.hash || '').replace(/^#/, '');
        const qIdx = hash.indexOf('?');
        if (qIdx >= 0) {
            const qs = new URLSearchParams(hash.slice(qIdx + 1));
            const t = qs.get('thread');
            if (t) _deepLinkThread = t;
            const tab = qs.get('tab');
            if (tab) _deepLinkTab = tab;
            const prompt = qs.get('prompt');
            if (prompt) _deepLinkPrompt = prompt;
            // ?new=1 → spin up a fresh chat thread on this page mount and
            // park on it. Used by the topbar "+ New chat" button and by
            // the "Just let me try it" path of the setup wizard.
            if (qs.get('new') === '1') _deepLinkNew = true;
        }
    } catch(_) {}
    this._deepLinkPrompt = _deepLinkPrompt;
    this._deepLinkNew = _deepLinkNew;
    this.activeTab = _deepLinkTab || this.getInitialTab();
    this._eventsBound = false;

    // Phase Q.4A Chat State
    this.projects = [];
    this.threads = [];
    this.messages = [];
    this.activeProjectId = localStorage.getItem('brain.activeProjectId') || null;
    this.activeThreadId = _deepLinkThread || localStorage.getItem('brain.activeThreadId') || null;
        this.pinnedResource = null;
    this.isSending = false;
    this.isMemoryPanelOpen = localStorage.getItem('brain.isMemoryPanelOpen') === 'true';
    this.isChatSidebarOpen = localStorage.getItem('brain.chatSidebarOpen') !== 'false';
    this.showSavedToast = false;
    this._profileSnapshot = null;
    this.connectedWebsites = [];

    // Search state
    this.chatSearchQ = '';
    this.chatSearchResults = [];
    this.chatSearchLoading = false;
    this.highlightMessageId = null;

    // Phase Q.4C actions
    this.showChatActions = false;
    this.shareModalOpen = false;
    this.activeShare = null;

    // Actionable Brain Chat state
    this.actionEdits = {};
    this.actionsDone = {};
  }

  getInitialTab() {
    // Default to 'chat' on first visit so a brand-new user lands directly
    // in the conversational surface — the headline experience now that
    // the sidebar has a single "The Brain" entry instead of split
    // Chat/Brain entries. Once the user clicks any tab, sessionStorage
    // persists their choice and overrides this fallback.
    return sessionStorage.getItem('brain.activeTab') || 'chat';
  }

  async setActiveTab(tab) {
    this.activeTab = tab;
    sessionStorage.setItem('brain.activeTab', tab);

    if (tab === 'chat') {
      try {
        await this.loadChatState();
      } catch (e) {
        console.error("[MEMORY] loadChatState failed:", e);
      }
    }

    this.render();
  }

  async mount(container) {
    this.container = container;
    await this.refresh();
  }

  async refresh() {
    try {
      this.memoryData = await client.getMemory();
      try { const pRes = await client.getPersonas(); this.personas = pRes.data || []; } catch(e) { this.personas = []; }

      // Fetch connected source statuses
      try {
        const [gRes, nRes, csRes] = await Promise.all([
          client.googleDocsStatus().catch(() => ({ connected: false })),
          client.notionStatus().catch(() => ({ connected: false })),
          client.getConnectedSources().catch(() => ({ sources: [] }))
        ]);
        this.googleConnected = !!(gRes && gRes.connected);
        this.notionConnected = !!(nRes && nRes.connected);
        this.connectedWebsites = (csRes.sources || []).filter(s => s.provider === 'website');
      } catch(e) { this.googleConnected = false; this.notionConnected = false; this.connectedWebsites = []; }

      // Fetch workspace media for Knowledge Vault
      try {
        if (window.__auth__?.workspace_id) {
          const mediaRes = await client.getWorkspaceMedia({});
          this.workspaceMedia = mediaRes.media || [];
        } else {
          this.workspaceMedia = [];
        }
      } catch(e) { this.workspaceMedia = []; }

      if (this.activeTab === 'chat') {
        await this.loadChatState();
        // One-shot: ?new=1 spins up a fresh thread and parks on it.
        // Done after loadChatState so we have an activeProjectId.
        if (this._deepLinkNew && this.activeProjectId) {
          this._deepLinkNew = false;
          try {
            const newThread = await client.createChatThread({ projectId: this.activeProjectId });
            this.activeThreadId = newThread.id;
            localStorage.setItem('brain.activeThreadId', newThread.id);
            this.messages = [];
            // Refresh thread list so the new one shows in the sidebar.
            const threadRes = await client.getChatThreads({ projectId: this.activeProjectId });
            this.threads = threadRes.data || [];
          } catch (e) {
            console.warn("[MEMORY] new-thread on ?new=1 failed:", e);
          }
          // Strip the one-shot ?new=1 from the URL so a refresh / share /
          // bookmark doesn't re-fire the create. replaceState doesn't
          // trigger hashchange, so no router re-mount loop.
          try {
            const cleaned = (location.hash || '').replace(/([?&])new=1\b/, '$1').replace(/[?&]$/, '');
            history.replaceState(null, '', location.pathname + location.search + cleaned);
          } catch (_) {}
        }
      }

      this.render();
    } catch (err) {
      console.error("[MEMORY_PAGE] refresh_fail:", err);
      this.container.innerHTML = `
        <div class="empty-state">
          <p>Failed to load Brand Brain: ${escapeHtml(err.message)}</p>
          <button class="btn btn-glass" onclick="window.location.reload()">Retry</button>
        </div>`;
    }
  }

  async loadChatState() {
    try {
      const projRes = await client.getChatProjects();
      this.projects = projRes.data || [];

      if (!this.activeProjectId && this.projects.length > 0) {
        const general = this.projects.find(p => p.name === 'General');
        this.activeProjectId = general ? general.id : this.projects[0].id;
      }

      if (this.activeProjectId) {
        const threadRes = await client.getChatThreads({ projectId: this.activeProjectId });
        this.threads = threadRes.data || [];

        if (!this.activeThreadId && this.threads.length > 0) {
          this.activeThreadId = this.threads[0].id;
        }

        if (this.activeThreadId) {
          const msgRes = await client.getChatMessages(this.activeThreadId);
          // Phase D: fetch pinned resource metadata for the chip
          try {
            const pinRes = await fetch(`/api/chat/threads/${encodeURIComponent(this.activeThreadId)}/pinned`, { credentials: 'include' });
            const pinJ = await pinRes.json().catch(() => null);
            if (pinJ && pinJ.ok) this.pinnedResource = pinJ.pinned || null;
          } catch (_) { this.pinnedResource = null; }
          this.messages = (msgRes.data || []).map(m => {
            if (m.role === 'assistant' && !m.actions) {
              var parsed = this._parseClientActions(m.content, m.role);
              if (parsed.length > 0) m.actions = parsed;
            }
            return m;
          });
        }
      }
    } catch (err) {
      console.error("[LOAD_CHAT_STATE_ERROR]", err);
    }
  }

  render() {
    if (!this.memoryData) {
      this.container.innerHTML = `
        <div style="padding: 24px;">
          <div class="skeleton skeleton-title"></div>
          <div class="skeleton skeleton-text"></div>
          <div style="display:grid;grid-template-columns:280px 1fr;gap:24px;margin-top:24px;">
            <div>
              <div class="skeleton skeleton-card"></div>
              <div class="skeleton skeleton-card"></div>
            </div>
            <div>
              <div class="skeleton skeleton-row"></div>
              <div class="skeleton skeleton-row"></div>
              <div class="skeleton skeleton-row"></div>
              <div class="skeleton skeleton-row"></div>
            </div>
          </div>
        </div>
      `;
      return;
    }

    const brainHealth = this.getBrainHealth();

    this.container.innerHTML = `
      <div id="page-memory" class="brain-layout ${this.activeTab === 'chat' ? 'brain-layout--chat' : ''}">
        ${renderPageHeader({
          extraClass: "brain-hero",
          eyebrow: "AI Configuration",
          title: "The Brain",
          subtitle: "Your brand's memory. Always learning, never forgetting.",
          titleClass: "brain-hero__title",
          subtitleClass: "brain-hero__subtitle",
          tourPlaylist: 'brain',
          rightHtml: `
                <div class="brain-health-badge" title="Brain completeness: ${brainHealth}%">
                  <svg class="brain-health-ring" width="52" height="52" viewBox="0 0 52 52">
                    <circle cx="26" cy="26" r="22" fill="none" stroke="rgba(255,255,255,0.08)" stroke-width="4"/>
                    <circle cx="26" cy="26" r="22" fill="none" stroke="url(#brain-health-grad)" stroke-width="4" stroke-linecap="round"
                      stroke-dasharray="${(brainHealth / 100) * 138.2} 138.2" transform="rotate(-90 26 26)"/>
                    <defs><linearGradient id="brain-health-grad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#8B5CF6"/><stop offset="100%" stop-color="#a78bfa"/></linearGradient></defs>
                  </svg>
                  <span class="brain-health-pct">${brainHealth}%</span>
                </div>
          `
        })}

        <div class="brain-tabs-wrap page-content" style="padding-bottom: 0;">
          <div class="page-shell">
            <div class="brain-tabs" role="tablist">
              <button class="brain-tab ${this.activeTab === 'brand' ? 'active' : ''}" role="tab" aria-selected="${this.activeTab === 'brand'}" data-tab="brand" data-testid="brain-tab-brand"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> Brand Strategy</button>
              <button class="brain-tab ${this.activeTab === 'documents' ? 'active' : ''}" role="tab" aria-selected="${this.activeTab === 'documents'}" data-tab="documents" data-testid="brain-tab-documents">${svgIcon('file-text', 14, 14)} Knowledge Base</button>
              <button class="brain-tab ${this.activeTab === 'chat' ? 'active' : ''}" role="tab" aria-selected="${this.activeTab === 'chat'}" data-tab="chat" data-testid="brain-tab-chat">${svgIcon('message', 14, 14)} Chat with Brain</button>
            </div>
          </div>
        </div>

        <div class="memory-container page-shell" data-testid="memory-container" style="padding-bottom: 48px;">
          ${this.activeTab === 'brand' ? this.renderViewBrand() : ''}
          ${this.activeTab === 'documents' ? this.renderViewDocuments() : ''}
          ${this.activeTab === 'chat' ? this.renderViewChat() : ''}
        </div>
      </div>

      ${this.shareModalOpen ? this.renderShareModal() : ''}
    `;

    this.bindEvents();

    // Auto-scroll chat to bottom if rendered
    if (this.activeTab === 'chat') {
      const history = this.container.querySelector("#chat-history");
      if (history) history.scrollTop = history.scrollHeight;
    }
  }

  getBrainHealth() {
    // Shared 7-field Brain-completeness model (B1 roadmap #5) — must show
    // the same number as the sidebar badge and the Command Center ring.
    if (!this.memoryData?.profile) return 0;
    return computeBrainScore(this.memoryData.profile).pct;
  }

  renderTabs() {
    return `
      <div class="brain-tabs-wrap">
        <div class="brain-content-wrap">
          <div class="brain-tabs" role="tablist" data-testid="brain-tabs">
            <button class="brain-tab" role="tab" aria-selected="${this.activeTab === 'brand'}" data-testid="brain-tab-brand" data-tab="brand">Brand</button>
            <button class="brain-tab" role="tab" aria-selected="${this.activeTab === 'documents'}" data-testid="brain-tab-documents" data-tab="documents">Documents</button>
            <button class="brain-tab" role="tab" aria-selected="${this.activeTab === 'chat'}" data-testid="brain-tab-chat" data-tab="chat">Chat</button>
          </div>
        </div>
      </div>
    `;
  }

  // G10-3: Brain completeness checklist
  renderBrainChecklist() {
    const profile = this.memoryData?.profile || {};
    const facts = this.memoryData?.facts || {};
    const documents = this.memoryData?.documents || [];
    const factCount = Object.values(facts).flat().length;

    const items = [
      { label: 'Mission/Positioning', filled: !!profile.mission?.trim(), hint: 'Describe your core mission' },
      { label: 'Target Audience', filled: !!profile.audience?.trim(), hint: 'Define who you talk to' },
      { label: 'Brand Voice', filled: !!profile.voice?.trim(), hint: 'Set your tone' },
      { label: 'Offers', filled: !!profile.offers_summary?.trim(), hint: 'List what you sell' },
      { label: 'Default CTA', filled: !!profile.cta?.trim(), hint: 'Set a call-to-action' },
      { label: 'Keywords', filled: !!profile.keywords?.trim(), hint: 'Add must-use words' },
      { label: 'Words to Avoid', filled: !!profile.avoid_words?.trim(), hint: 'List words to keep out' },
      { label: 'Memory Facts (at least 1)', filled: factCount > 0, hint: 'Add a fact below' },
      { label: 'Knowledge Base (at least 1 document)', filled: documents.length > 0, hint: 'Upload in Documents tab' },
    ];

    const allDone = items.every(i => i.filled);
    if (allDone) return '';

    const filledCount = items.filter(i => i.filled).length;
    const checkSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
    const circleSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/></svg>';

    return `
      <div class="brain-checklist-card">
        <div class="brain-checklist-title">Brain Completeness (${filledCount}/${items.length})</div>
        <div class="brain-checklist-items">
          ${items.map(item => `
            <div class="brain-checklist-item ${item.filled ? 'done' : ''}">
              ${item.filled ? checkSvg : circleSvg}
              <span>${escapeHtml(item.label)}</span>
              ${!item.filled ? `<span class="brain-checklist-hint">${escapeHtml(item.hint)}</span>` : ''}
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  renderQuickStart() {
    const brainHealth = this.getBrainHealth();
    const dismissed = sessionStorage.getItem('brain.quickStartDismissed') === 'true';
    if (brainHealth >= 15 || dismissed) return '';

    return `
      <div class="brain-quickstart" data-testid="brain-quickstart">
        <div class="brain-quickstart__header">
          <div class="brain-quickstart__icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
          </div>
          <div>
            <h3 class="brain-quickstart__title">Get started in 60 seconds</h3>
            <p class="brain-quickstart__subtitle">Answer 3 quick questions and your Brain is ready to create content.</p>
          </div>
        </div>
        <div class="brain-quickstart__fields">
          <div class="brain-quickstart__field">
            <label class="brain-quickstart__label">What's your brand or business called?</label>
            <input type="text" id="qs-brand-name" class="brain-quickstart__input" placeholder="e.g. Acme Creative Studio" />
          </div>
          <div class="brain-quickstart__field">
            <label class="brain-quickstart__label">Who are you creating content for?</label>
            <input type="text" id="qs-audience" class="brain-quickstart__input" placeholder="e.g. Small business owners who want to grow on social media" />
          </div>
          <div class="brain-quickstart__field">
            <label class="brain-quickstart__label">How should your content sound?</label>
            <select id="qs-voice" class="brain-quickstart__input">
              <option value="">Select a voice...</option>
              <option value="Professional">Professional</option>
              <option value="Casual">Casual</option>
              <option value="Witty">Witty</option>
              <option value="Bold">Bold</option>
              <option value="Inspirational">Inspirational</option>
              <option value="Educational">Educational</option>
              <option value="Provocative">Provocative</option>
              <option value="Storytelling">Storytelling</option>
              <option value="Other">Other...</option>
            </select>
            <input type="text" id="qs-voice-custom" class="gen-tone-custom" placeholder="e.g. Sarcastic, Gen-Z, Corporate-but-fun..." style="display:none;margin-top:8px;" />
          </div>
        </div>
        <div class="brain-quickstart__actions">
          <button class="btn btn-primary" id="qs-save-btn" data-testid="qs-save-btn">Save &amp; Continue</button>
          <button class="btn-link brain-quickstart__skip" id="qs-skip-btn" data-testid="qs-skip-btn">Skip &mdash; show me everything</button>
        </div>
      </div>
    `;
  }

  renderViewBrand() {
    const { profile } = this.memoryData;
    const brainHealth = this.getBrainHealth();
    const factCount = this.memoryData.facts ? Object.values(this.memoryData.facts).flat().length : 0;

    return `
      <div data-testid="brain-view-brand" class="brain-view">
        <div class="brain-view-shell">
          <div class="brain-layout-2col">
          <div class="brain-main">
          ${this.renderQuickStart()}
          ${this.renderBrainChecklist()}
          <!-- Save bar -->
          <div class="brain-save-bar" data-testid="brand-profile-card">
            <div class="brain-save-bar__left">
              ${svgIcon('users', 18, 18)}
              <span class="brain-save-bar__label">Brand Identity</span>
              <span class="brain-save-bar__stats">${brainHealth}% complete &middot; ${factCount} facts</span>
            </div>
            <div class="brain-save-bar__right">
              <span id="save-status" class="status-msg hide">Saved!</span>
              <button class="btn btn-primary" id="save-profile" data-testid="save-profile">${svgIcon('file', 14, 14)} Save Identity</button>
            </div>
          </div>

          <!-- URL Scanner — auto-fill from website -->
          <div class="brain-url-scanner">
            <div class="brain-url-scanner__header" id="brain-scanner-toggle" style="cursor:pointer;display:flex;align-items:center;gap:8px;">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <span style="font-weight:600;font-size:14px;">Auto-fill from Website</span>
              <span style="font-size:12px;color:var(--text-muted);">— scan your site to update brand fields</span>
              <svg class="brain-url-scanner__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;transition:transform 0.2s;"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <div class="brain-url-scanner__body hide" id="brain-scanner-body">
              <div class="brain-url-scanner__row">
                <input type="url" id="brain-scanner-url" class="brain-url-scanner__input" placeholder="https://yoursite.com" />
                <button id="brain-scanner-btn" class="btn btn-primary btn-sm">Scan & Suggest</button>
              </div>
              <div id="brain-scanner-status" class="brain-url-scanner__status"></div>
              <div id="brain-scanner-results" class="brain-url-scanner__results hide"></div>
            </div>
          </div>

          <!-- Stacked Fields -->
            <div class="brain-field-section">
              <label class="brain-field-section__label"><span class="brain-field-section__icon">${svgIcon('target', 16, 16)}</span> Mission / Positioning</label>
              <textarea id="profile-mission" class="brain-field-section__input" placeholder="What is the core mission?">${profile.mission || ""}</textarea>
            </div>
            <div class="brain-field-section">
              <label class="brain-field-section__label"><span class="brain-field-section__icon">${svgIcon('users', 16, 16)}</span> Target Audience</label>
              <textarea id="profile-audience" class="brain-field-section__input" placeholder="Who are we talking to?">${profile.audience || ""}</textarea>
            </div>
            <div class="brain-field-section">
              <label class="brain-field-section__label"><span class="brain-field-section__icon">${svgIcon('message', 16, 16)}</span> Brand Voice / Tone</label>
              <textarea id="profile-voice" class="brain-field-section__input" placeholder="How do we sound? (e.g., Professional yet witty)">${profile.voice || ""}</textarea>
            </div>
            <div class="brain-field-section">
              <label class="brain-field-section__label"><span class="brain-field-section__icon">${svgIcon('briefcase', 16, 16)}</span> Core Offer(s)</label>
              <textarea id="profile-offers" class="brain-field-section__input" placeholder="What do we sell?">${profile.offers_summary || ""}</textarea>
            </div>
            <div class="brain-field-section">
              <label class="brain-field-section__label"><span class="brain-field-section__icon">${svgIcon('megaphone', 16, 16)}</span> Standard CTA</label>
              <input type="text" id="profile-cta" class="brain-field-section__input" value="${profile.cta || ""}" placeholder="Click here to learn more" />
            </div>
            <div class="brain-field-row">
              <div class="brain-field-section">
                <label class="brain-field-section__label"><span class="brain-field-section__icon">${svgIcon('hash', 16, 16)}</span> Keywords (Always use)</label>
                <textarea id="profile-keywords" class="brain-field-section__input" placeholder="AI-powered, Scale, Premium">${profile.keywords || ""}</textarea>
              </div>
              <div class="brain-field-section">
                <label class="brain-field-section__label"><span class="brain-field-section__icon">${svgIcon('x-circle', 16, 16)}</span> Avoid Words</label>
                <textarea id="profile-avoid" class="brain-field-section__input" placeholder="Cheap, Hacky, Scam">${profile.avoid_words || ""}</textarea>
              </div>
            </div>

          <!-- Test Your Brain CTA -->
          <div class="brain-test-cta">
            <span class="brain-test-cta__text">Ready to see your Brain in action?</span>
            <button class="btn btn-primary brain-test-cta__btn" data-action="test-brain">Chat with Brain</button>
          </div>

          <!-- Audience Personas Section -->
          <section class="brain-card" data-testid="audience-personas-card">
            <div class="card-header" style="display:flex;justify-content:space-between;align-items:center;">
              <h3>Audience Personas</h3>
              <button class="btn btn-glass btn-sm" id="add-persona-btn">+ Add Persona</button>
            </div>
            <div class="card-body">
              <p style="font-size:12px;color:var(--text-muted);margin-bottom:12px;">Define who you create content for. Personas are injected into every AI generation.</p>
              <div id="persona-list">
                ${(this.personas || []).map(p => `
                  <div class="persona-card" data-persona-id="${p.id}">
                    <button class="btn-ghost persona-card__delete" data-action="delete-persona" data-id="${p.id}" title="Delete persona">&times;</button>
                    <div class="persona-card__name">${escapeHTML(p.name)}</div>
                    ${p.demographics ? `<div class="persona-card__field"><span class="persona-card__label">Demographics:</span> ${escapeHTML(p.demographics)}</div>` : ''}
                    ${p.pain_points ? `<div class="persona-card__field"><span class="persona-card__label">Pain Points:</span> ${escapeHTML(p.pain_points)}</div>` : ''}
                    ${p.goals ? `<div class="persona-card__field"><span class="persona-card__label">Goals:</span> ${escapeHTML(p.goals)}</div>` : ''}
                    ${p.language_style ? `<div class="persona-card__field"><span class="persona-card__label">Language:</span> ${escapeHTML(p.language_style)}</div>` : ''}
                    ${p.platforms ? `<div class="persona-card__field"><span class="persona-card__label">Platforms:</span> ${escapeHTML(p.platforms)}</div>` : ''}
                  </div>
                `).join('')}
                ${(this.personas || []).length === 0 ? '<div style="font-size:13px;color:var(--text-muted);padding:8px 0;">No personas yet. Add one to improve content targeting.</div>' : ''}
              </div>
              <div id="persona-form" style="display:none;margin-top:12px;">
                <div style="display:grid;gap:10px;">
                  <input type="text" id="persona-name" class="input" placeholder="Persona name (e.g., Marketing Manager Mike)" />
                  <input type="text" id="persona-demographics" class="input" placeholder="Demographics (e.g., 30-45, US, mid-level)" />
                  <input type="text" id="persona-pain-points" class="input" placeholder="Pain points (e.g., No time for content, inconsistent brand voice)" />
                  <input type="text" id="persona-goals" class="input" placeholder="Goals (e.g., Scale content output, grow following)" />
                  <input type="text" id="persona-language" class="input" placeholder="Language style (e.g., Professional but casual)" />
                  <input type="text" id="persona-platforms" class="input" placeholder="Preferred platforms (e.g., LinkedIn, X)" />
                  <div style="display:flex;gap:8px;">
                    <button class="btn btn-primary btn-sm" id="persona-save-btn">Save Persona</button>
                    <button class="btn btn-ghost btn-sm" id="persona-cancel-btn">Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Memory Facts Section -->
          <section class="brain-card brain-facts-section" data-testid="memory-facts-card">
            <div class="card-header">
              <h3>Memory Facts</h3>
            </div>
            <div class="card-body">
              <div class="add-fact-box">
                <div class="form-group">
                  <label>Type</label>
                  <select id="fact-type" data-testid="fact-type-select">
                    <option value="goal">Goal</option>
                    <option value="offer">Specific Offer</option>
                    <option value="rule_do">Writing Rule (Do)</option>
                    <option value="rule_dont">Writing Rule (Don't)</option>
                    <option value="general">General Truth</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Content</label>
                  <textarea id="new-fact-content" placeholder="Add a specific fact or rule..." data-testid="new-fact-input"></textarea>
                </div>
                <button class="btn btn-secondary" id="add-fact" data-testid="add-fact-btn">Add to Memory</button>
              </div>

              <div class="facts-list" id="facts-list">
                ${this.renderFacts()}
              </div>
            </div>
          </section>
          </div>

          <aside class="brain-sidebar-col">
            <div class="doc-repo-card">
              <div class="doc-repo-header"><span class="doc-repo-title">Quick Reference</span></div>
              <div class="brain-quick-ref-list" style="padding:12px 16px;">
                <div class="brain-quick-ref-item"><span class="brain-quick-ref-bullet"></span><span>${profile.mission ? escapeHTML(profile.mission.substring(0, 50)) + (profile.mission.length > 50 ? '...' : '') : 'No mission set'}</span></div>
                <div class="brain-quick-ref-item"><span class="brain-quick-ref-bullet"></span><span>${profile.audience ? escapeHTML(profile.audience.substring(0, 50)) + (profile.audience.length > 50 ? '...' : '') : 'No audience set'}</span></div>
                <div class="brain-quick-ref-item"><span class="brain-quick-ref-bullet"></span><span>${profile.voice ? escapeHTML(profile.voice.substring(0, 50)) + (profile.voice.length > 50 ? '...' : '') : 'No voice set'}</span></div>
              </div>
            </div>
            <div class="doc-repo-card">
              <div class="doc-repo-header"><span class="doc-repo-title">Brand Stats</span></div>
              <div class="brain-quick-ref-list" style="padding:12px 16px;">
                <div class="brain-quick-ref-item"><span class="brain-quick-ref-bullet"></span><span><strong>${factCount}</strong> memory facts</span></div>
                <div class="brain-quick-ref-item"><span class="brain-quick-ref-bullet"></span><span><strong>${profile.keywords ? profile.keywords.split(',').filter(k => k.trim()).length : 0}</strong> keywords</span></div>
                <div class="brain-quick-ref-item"><span class="brain-quick-ref-bullet"></span><span><strong>${profile.avoid_words ? profile.avoid_words.split(',').filter(k => k.trim()).length : 0}</strong> avoid words</span></div>
              </div>
            </div>
            <div class="doc-repo-card">
              <div class="doc-repo-header"><span class="doc-repo-title">Recent Documents</span></div>
              <div class="doc-repo-list">
                ${(this.memoryData.documents || []).length === 0 ? '<div style="padding:12px 16px;font-size:12px;color:var(--text-muted);">No documents yet</div>' : (this.memoryData.documents || []).slice(0, 5).map(d => `
                  <div class="doc-repo-item">
                    <span class="doc-repo-icon">${d.file_type === 'pdf' ? svgIcon('file', 14, 14) : d.file_type === 'txt' ? svgIcon('file-text', 14, 14) : svgIcon('paperclip', 14, 14)}</span>
                    <span class="doc-repo-name">${escapeHTML(d.filename)}</span>
                    <span class="doc-repo-meta">${this.formatSize(d.file_size)}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          </aside>
          </div>
      </div>
    `;
  }

  renderViewDocuments() {
    const docs = this.memoryData.documents || [];
    const typeMap = {};
    docs.forEach(d => { typeMap[d.file_type] = (typeMap[d.file_type] || 0) + 1; });
    const recentDocs = docs.slice(0, 5);

    return `
      <div data-testid="brain-view-documents" class="brain-view">
        <div class="brain-view-shell">
          <div class="brain-view-header">
            <h2 class="section-title">Knowledge Vault</h2>
            <p class="section-subtitle">Upload docs your Brain can reference.</p>
          </div>
          <div class="brain-layout-2col">
          <div class="brain-main">
          <div class="brain-grid brain-section-sep">
            <section class="brain-card full-width" data-testid="brain-vault-card">
              <div class="card-body">
                <!-- Inline Error Banner for Docs -->
                <div id="vault-error-banner" class="vault-error-banner hide"></div>

                <div class="vault-url-scan">
                  <div class="vault-url-scan__row">
                    <input type="url" id="vault-scan-url-input" class="vault-url-scan__input" placeholder="Paste your website URL to scan..." />
                    <button id="vault-scan-url-btn" class="btn btn-primary btn-sm vault-url-scan__btn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                      Scan Website
                    </button>
                  </div>
                  <div id="vault-scan-status" class="vault-url-scan__status"></div>
                </div>

                <div class="vault-upload-zone" id="vault-upload-zone" data-testid="brain-vault-upload">
                  <p>Drag & drop documents or click to upload</p>
                  <input type="file" id="brain-vault-file-input" class="hide" data-testid="brain-vault-file-input" />
                  <button class="btn btn-secondary" id="brain-vault-upload-btn" data-testid="brain-vault-upload-btn">Select File</button>
                </div>

                <div class="vault-list-container">
                  <table class="vault-table" data-testid="brain-vault-list">
                    <thead>
                      <tr>
                        <th>Filename</th>
                        <th>Type</th>
                        <th>Size</th>
                        <th>Uploaded</th>
                        <th class="analytics-th--right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${this.renderDocuments()}
                    </tbody>
                  </table>
                </div>

                <div style="margin-top:12px;display:flex;justify-content:flex-end">
                  <button class="btn btn-ghost btn-sm" id="vault-trash-toggle" style="font-size:11px;opacity:0.7">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-1px;margin-right:3px"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    ${this.showVaultTrash ? 'Hide Trash' : 'Show Trash'}
                  </button>
                </div>

                ${this.showVaultTrash ? `
                <div class="vault-trash-section" style="margin-top:12px;padding:12px;border:1px dashed var(--border);border-radius:8px;background:var(--surface-2)">
                  <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
                    <span style="font-size:12px;font-weight:600;color:var(--text-muted)">Trash</span>
                    ${(this.vaultTrash || []).length > 0 ? `<button class="btn btn-ghost btn-sm" id="vault-empty-trash" style="font-size:10px;color:var(--danger)">Empty Trash</button>` : ''}
                  </div>
                  ${(this.vaultTrash || []).length === 0
                    ? '<div style="font-size:11px;color:var(--text-muted);padding:8px 0">Trash is empty</div>'
                    : `<table class="vault-table" style="font-size:12px"><tbody>
                      ${this.vaultTrash.map(d => `
                        <tr class="vault-item">
                          <td>${escapeHtml(d.filename)}</td>
                          <td><span class="vault-source-badge">${(d.file_type || '').toUpperCase()}</span></td>
                          <td>${this.formatSize(d.file_size)}</td>
                          <td>
                            <button class="btn btn-ghost btn-sm" data-action="restore-doc" data-id="${d.id}" style="font-size:10px">Restore</button>
                            <button class="btn btn-ghost btn-sm" data-action="perm-delete-doc" data-id="${d.id}" style="font-size:10px;color:var(--danger)">Delete</button>
                          </td>
                        </tr>
                      `).join('')}
                    </tbody></table>`
                  }
                </div>
                ` : ''}
              </div>
            </section>

            ${this.workspaceMedia && this.workspaceMedia.length > 0 ? `
            <section class="brain-card full-width" style="margin-top:16px">
              <div class="card-body">
                <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
                  <div>
                    <div style="font-size:14px;font-weight:700;color:var(--text)">Media Library</div>
                    <div style="font-size:12px;color:var(--text-muted);margin-top:2px">${this.workspaceMedia.length} file${this.workspaceMedia.length !== 1 ? 's' : ''} in workspace</div>
                  </div>
                  <button class="btn btn-ghost btn-sm" id="brain-open-media-library" style="font-size:11px">Upload / Browse</button>
                </div>
                <div class="vault-media-grid">
                  ${this.workspaceMedia.map(m => {
                    const isImage = /image/i.test(m.fileType || m.file_type || '');
                    const isVideo = /video/i.test(m.fileType || m.file_type || '');
                    const url = m.url || `/uploads/workspace-media/${m.filename}`;
                    const name = m.originalName || m.original_name || m.filename;
                    const size = this.formatSize(m.fileSize || m.file_size || 0);
                    return `
                      <div class="vault-media-item" data-media-id="${m.id}">
                        <div class="vault-media-thumb">
                          ${isImage ? `<img src="${url}" alt="${escapeHtml(name)}" loading="lazy">` :
                            isVideo ? `<div style="position:relative;width:100%;height:100%;background:#000">
                              <video src="${url}#t=0.1" muted preload="metadata" style="width:100%;height:100%;object-fit:cover"></video>
                              <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="white" style="filter:drop-shadow(0 1px 3px rgba(0,0,0,0.5))"><path d="M8 5v14l11-7z"/></svg>
                              </div>
                            </div>` :
                            `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:var(--surface-2);color:var(--text-muted);font-size:20px">${svgIcon('file', 24, 24)}</div>`
                          }
                        </div>
                        <div class="vault-media-info">
                          <div class="vault-media-name" title="${escapeHtml(name)}">${escapeHtml(name)}</div>
                          <div class="vault-media-meta">${size}</div>
                        </div>
                        <button class="vault-media-delete" data-action="delete-workspace-media" data-media-id="${m.id}" title="Delete">&times;</button>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            </section>
            ` : ''}
          </div>
          </div>
          <aside class="brain-sidebar-col">
            <div class="connected-sources-card">
              <div class="connected-sources-header">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                <span>Connected Sources</span>
              </div>
              <div class="connected-sources-list" id="connected-sources-list">
                <div class="connected-source-item">
                  <span class="connected-source-label">Google Docs</span>
                  ${this.googleConnected
                    ? '<span class="connected-source-badge connected">Live</span><button class="connected-source-action" data-action="pick-google-docs" title="Select docs to sync">+</button>'
                    : '<a href="#settings" class="connected-source-connect">Connect</a>'}
                </div>
                <div class="connected-source-item">
                  <span class="connected-source-label">Notion</span>
                  ${this.notionConnected
                    ? '<span class="connected-source-badge connected">Live</span><button class="connected-source-action" data-action="pick-notion-pages" title="Select pages to sync">+</button>'
                    : '<a href="#settings" class="connected-source-connect">Connect</a>'}
                </div>
                ${(this.connectedWebsites || []).map(ws => `
                <div class="connected-source-item">
                  <span class="connected-source-label" title="${escapeHtml(ws.scan_url || '')}">${escapeHtml(ws.hostname || 'Website')}</span>
                  <span class="connected-source-badge connected">Live</span>
                  <button class="connected-source-action" data-action="rescan-source" data-source-id="${ws.id}" title="Rescan for updates">&#x21BB;</button>
                  <button class="connected-source-action connected-source-action--danger" data-action="disconnect-source" data-source-id="${ws.id}" title="Disconnect">&times;</button>
                </div>
                `).join('')}
              </div>
            </div>
            <div class="doc-repo-card">
              <div class="doc-repo-header">
                <span class="doc-repo-title">Recent Documents</span>
              </div>
              <div class="doc-repo-list">
                ${recentDocs.length === 0 ? '<div style="padding:12px 16px;font-size:12px;color:var(--text-muted);">No documents yet</div>' : recentDocs.map(d => `
                  <div class="doc-repo-item">
                    <span class="doc-repo-icon">${d.file_type === 'pdf' ? svgIcon('file', 14, 14) : d.file_type === 'txt' ? svgIcon('file-text', 14, 14) : svgIcon('paperclip', 14, 14)}</span>
                    <span class="doc-repo-name">${escapeHTML(d.filename)}</span>
                    <span class="doc-repo-meta">${this.formatSize(d.file_size)}</span>
                  </div>
                `).join('')}
              </div>
            </div>
            <div class="brain-quick-ref">
              <div class="brain-quick-ref-title">Vault Summary</div>
              <div class="brain-quick-ref-list">
                <div class="brain-quick-ref-item"><span class="brain-quick-ref-bullet"></span><span>${docs.length} documents total</span></div>
                ${Object.entries(typeMap).map(([type, count]) => `
                  <div class="brain-quick-ref-item"><span class="brain-quick-ref-bullet"></span><span>${count} ${type.toUpperCase()} file${count > 1 ? 's' : ''}</span></div>
                `).join('')}
              </div>
            </div>
          </aside>
          </div>
        </div>
      </div>
    `;
  }

  renderViewChat() {
    return `
      <div data-testid="brain-view-chat" class="brain-view">
        <div class="brain-view-shell">
          <div class="brain-chat-workspace ${this.isMemoryPanelOpen ? 'show-memory' : ''} ${this.isChatSidebarOpen ? '' : 'sidebar-collapsed'}" data-testid="brain-chat-workspace">
            <!-- Sidebar -->
            <aside class="chat-sidebar">
              <div class="chat-sidebar-header">
                <div class="project-selector-wrapper">
                  <select data-testid="brain-project-select" id="project-select">
                    ${this.projects.map(p => `<option value="${p.id}" ${p.id === this.activeProjectId ? 'selected' : ''}>${escapeHTML(p.name)}</option>`).join('')}
                  </select>
                  <button class="btn-icon" data-testid="brain-project-new" id="new-project-btn" title="New Project">+</button>
                </div>
                <button class="btn-new-chat" data-testid="brain-chat-new" id="new-chat-btn">
                  <span>+</span> New Chat
                </button>
              </div>

              <div class="chat-search">
                <input type="text" 
                       id="chat-search-input" 
                       data-testid="brain-chat-search" 
                       placeholder="Search messages..." 
                       value="${escapeHTML(this.chatSearchQ)}" />
                <div class="chat-search-results ${this.chatSearchResults.length > 0 ? 'visible' : ''}">
                  ${this.chatSearchLoading ? '<div class="search-loading">Searching...</div>' : ''}
                  ${this.chatSearchResults.map(res => `
                    <div class="chat-search-result" 
                         data-testid="brain-search-result"
                         data-threadid="${res.threadId}" 
                         data-messageid="${res.messageId}">
                      <div class="search-res-title">${escapeHTML(res.title)}</div>
                      <div class="search-res-snippet">${escapeHTML(res.snippet)}</div>
                    </div>
                  `).join('')}
                </div>
              </div>
              
              <div class="thread-list" data-testid="brain-thread-list">
                ${this.threads.length === 0 ? '<div class="empty-state">No conversations yet — hit "New Chat" and ask your Brain anything about your brand.</div>' : ''}
                ${this.threads.map(t => `
                  <div class="thread-item ${t.id === this.activeThreadId ? 'active' : ''}" 
                       data-testid="brain-thread-item" 
                       data-threadid="${t.id}">
                    <div class="thread-item-title">${escapeHTML(t.title)}</div>
                    <div class="thread-actions">
                      <button data-action="rename-thread" data-id="${t.id}" title="Rename">${svgIcon('pencil', 13, 13)}</button>
                      <button data-action="delete-thread" data-id="${t.id}" title="Delete" aria-label="Delete">${svgIcon('trash', 13, 13)}</button>
                    </div>
                  </div>
                `).join('')}
              </div>
            </aside>

            <!-- Main Panel -->
            <main class="chat-main">
              <header class="chat-header">
                 <div class="chat-header-left">
                    <button class="btn-sidebar-toggle" id="chat-sidebar-toggle" title="${this.isChatSidebarOpen ? 'Hide sidebar' : 'Show sidebar'}"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg></button>
                    <h3 id="active-thread-title">${escapeHTML(this.threads.find(t => t.id === this.activeThreadId)?.title || 'Chat')}</h3>
                    ${(() => {
                      const pr = this.pinnedResource;
                      if (!pr || !pr.meta) return '';
                      const threadTitle = (this.threads.find(t => t.id === this.activeThreadId)?.title || '').replace(/^About:\s*/i, '').trim().toLowerCase();
                      const lbl = (pr.meta.label || '').trim().toLowerCase();
                      // Hide the chip when the thread title already says the same thing.
                      const sharesA = threadTitle && lbl && lbl.length >= 10 && threadTitle.includes(lbl.slice(0, 40));
                      const sharesB = threadTitle && lbl && threadTitle.length >= 10 && lbl.includes(threadTitle.slice(0, 40));
                      if (threadTitle === lbl || sharesA || sharesB) return '';
                      return `
                      <div class="brain-pinned-chip" title="This chat is pinned to this item. Messages reference its current state.">
                        <span class="brain-pinned-chip__label">Working on</span>
                        <span class="brain-pinned-chip__type">${escapeHTML(pr.type || '')}</span>
                        <span class="brain-pinned-chip__sep">·</span>
                        <span class="brain-pinned-chip__title">${escapeHTML((pr.meta.label || '').slice(0, 80))}</span>
                      </div>
                      `;
                    })()}
                 </div>
                 <div class="chat-header-right" style="display:flex; align-items:center; gap:8px;">
                    ${this.activeThreadId ? `
                      <div class="chat-actions-wrapper">
                        <button id="chat-actions-btn" class="btn-icon" title="Thread actions">⋮</button>
                        <div id="chat-actions-menu" class="chat-actions-menu ${this.showChatActions ? 'visible' : ''}">
                          <button class="chat-actions-item" data-action="export-thread-md">
                            <span class="icon">${svgIcon('file-text', 15, 15)}</span> Export Thread (MD)
                          </button>
                          <button class="chat-actions-item" data-action="export-thread-json">
                            <span class="icon">${svgIcon('package', 15, 15)}</span> Export Thread (JSON)
                          </button>
                          <button class="chat-actions-item" data-action="export-project-md">
                            <span class="icon">${svgIcon('folder', 15, 15)}</span> Export Project (MD)
                          </button>
                          <button class="chat-actions-item" data-action="export-project-json">
                            <span class="icon">${svgIcon('archive', 15, 15)}</span> Export Project (JSON)
                          </button>
                          <hr style="border:0; border-top:1px solid var(--border); margin:4px 0;">
                          <button class="chat-actions-item" data-action="share-thread">
                            <span class="icon">${svgIcon('link', 15, 15)}</span> Share Thread
                          </button>
                        </div>
                      </div>
                    ` : ''}
                    <button class="btn-header-action" data-testid="brain-memory-toggle" id="memory-toggle-btn">
                      ${this.isMemoryPanelOpen ? 'Close Memory' : 'Memory Manager'}
                    </button>
                 </div>
              </header>

              <div class="chat-history" id="chat-history" data-testid="brain-chat-history">
                ${this.messages.length === 0 ? `
                  <div class="chat-message ai">
                     <div class="message-inner">
                        <div class="message-avatar">B</div>
                        <div class="message-bubble">
                          Hello! I am your brand's Brain. Ask me anything about your voice, strategy, or documents.
                        </div>
                     </div>
                  </div>
                  <div class="brain-chat-suggestions">
                    <p class="brain-chat-suggestions-label">Try asking your Brain...</p>
                    <div class="brain-chat-suggestions-grid">
                      <button class="brain-chat-suggestion" data-prompt="Write a week of LinkedIn posts about my core offer"><svg class="bcs-icon" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>Write a week of LinkedIn posts</button>
                      <button class="brain-chat-suggestion" data-prompt="Review my brand voice for consistency across platforms"><svg class="bcs-icon" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>Review my brand voice consistency</button>
                      <button class="brain-chat-suggestion" data-prompt="Suggest 5 content themes for this month based on my audience"><svg class="bcs-icon" viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>Suggest 5 content themes</button>
                      <button class="brain-chat-suggestion" data-prompt="Create a product launch campaign outline for my main offer"><svg class="bcs-icon" viewBox="0 0 24 24"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>Create a launch campaign outline</button>
                      <button class="brain-chat-suggestion" data-prompt="What tone should I use for my target audience on each platform?"><svg class="bcs-icon" viewBox="0 0 24 24"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>Best tone for my audience</button>
                      <button class="brain-chat-suggestion" data-prompt="Analyze what's working in my content strategy and suggest improvements"><svg class="bcs-icon" viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>Analyze my content strategy</button>
                    </div>
                  </div>
                ` : ''}
                ${this.renderMessages()}
              </div>

              <div class="chat-composer">
                <div class="composer-inner">
                  <textarea 
                    id="chat-input" 
                    data-testid="brain-chat-input" 
                    placeholder="Ask the brain..."
                    rows="1"></textarea>
                  <button class="btn-send" id="chat-send" data-testid="brain-chat-send" ${this.isSending ? 'disabled' : ''}>
                     <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.333 1.333L14.666 8L1.333 14.667V8.667L10.666 8L1.333 7.333V1.333Z" fill="currentColor"/></svg>
                  </button>
                </div>
                ${this.showSavedToast ? `<div class="chat-toast">${svgIcon('check', 14, 14)} Saved</div>` : ''}
              </div>
            </main>

            <!-- Memory Manager Panel -->
            <aside class="chat-memory-panel ${this.isMemoryPanelOpen ? 'open' : ''}" data-testid="brain-memory-panel">
              <div class="memory-panel-header">
                <h4>Memory Manager</h4>
              </div>
              <div class="memory-panel-body">
                <div class="memory-quick-add">
                  <select id="panel-fact-type">
                    <option value="goal">Goal</option>
                    <option value="offer">Offer</option>
                    <option value="rule_do">Rule (Do)</option>
                    <option value="rule_dont">Rule (Don't)</option>
                    <option value="general">General</option>
                  </select>
                  <textarea id="panel-fact-content" placeholder="Type a fact and press Enter..." rows="2"></textarea>
                  <button class="btn btn-secondary btn-sm" id="panel-add-fact">Add Fact</button>
                </div>
                <div class="memory-panel-list">
                  ${this.renderMemoryManagerList()}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    `;
  }

  renderMemoryManagerList() {
    if (!this.memoryData) return '<div class="loading">Loading...</div>';
    const { facts } = this.memoryData;
    const all = [
      ...facts.goals.map(f => ({ ...f, type: 'goal', label: 'Goal' })),
      ...facts.offers.map(f => ({ ...f, type: 'offer', label: 'Offer' })),
      ...facts.rules_do.map(f => ({ ...f, type: 'rule_do', label: 'Do' })),
      ...facts.rules_dont.map(f => ({ ...f, type: 'rule_dont', label: 'Don\'t' })),
      ...(facts.other || []).map(f => ({ ...f, type: 'other', label: 'Other' }))
    ];

    if (all.length === 0) return '<div class="empty-state">Nothing saved yet. Facts are short truths about your brand — a goal, an offer, a do-or-don\'t rule — and every one sharpens your drafts. Add your first one above.</div>';

    return all.map(f => `
      <div class="memory-item">
        <div class="memory-item-meta">
          <span class="badge ${f.type}">${f.label}</span>
          <button class="btn-delete-fact" data-id="${f.id}" title="Delete" aria-label="Delete">${svgIcon('trash', 13, 13)}</button>
        </div>
        <div class="memory-item-content">${escapeHTML(f.content)}</div>
      </div>
    `).join('');
  }

  renderMessages() {
    var html = '';
    for (var mi = 0; mi < this.messages.length; mi++) {
      var m = this.messages[mi];
      var actionsHtml = '';
      var actions = m.actions || this._parseClientActions(m.content, m.role);
      if (actions.length > 0 && m.role === 'ai') {
        for (var ai = 0; ai < actions.length; ai++) {
          actionsHtml += this.renderActionBlock(actions[ai], m.id, ai);
        }
      }
      // Strip :::action blocks from display text (they're rendered separately as action cards)
      var displayContent = m.content;
      if (m.role === 'ai' && displayContent && displayContent.indexOf(':::action') !== -1) {
        displayContent = displayContent.replace(/:::action\s*\n[\s\S]*?\n:::/g, '').trim();
      }
      var renderedContent = m.role === 'ai' ? renderChatMarkdown(displayContent) : escapeHTML(displayContent);
      // Source chip — only shown when a turn came from outside Heist Brain
      // (MCP connector from Claude / ChatGPT / etc. via append_to_thread).
      var clientLabel = m.client_label || '';
      var sourceChip = '';
      if (clientLabel && clientLabel !== 'heist_brain') {
        var pretty = clientLabel.replace(/^agent:/, '').replace(/_/g, ' ');
        sourceChip = '<span class="msg-source-chip" title="This turn came from ' + escapeHTML(clientLabel) + '">via ' + escapeHTML(pretty) + '</span>';
      }
      html += `
      <div class="chat-message ${m.role} ${m.id === this.highlightMessageId ? 'highlight' : ''}" data-id="${m.id}" data-source="${escapeHTML(clientLabel)}">
        <div class="message-inner">
          <div class="message-avatar">${m.role === 'user' ? 'U' : 'B'}</div>
          <div class="message-bubble">
            ${sourceChip}
            ${m.role !== 'user' ? '<button class="chat-save-btn" data-action="chat-save-quick" data-content="' + encodeURIComponent(m.content) + '" title="Save to Brain"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg></button>' : ''}
            <div class="msg-content">${renderedContent}</div>
            ${actionsHtml}
            ${m.stub ? '<div class="quota-warning" data-testid="quota-warning">&#9888;&#65039; Quota/Billing fallback active (RID: ' + escapeHTML(m.request_id || 'N/A') + ')</div>' : ''}
            <div class="message-actions">
              <button class="btn-msg-action" data-action="copy-msg" data-content="${encodeURIComponent(m.content)}">Copy</button>
              <button class="btn-msg-action" data-action="save-to-brain" data-content="${encodeURIComponent(m.content)}">Save to Brain</button>
            </div>
          </div>
        </div>
      </div>`;
    }
    if (this.isSending) {
      html += `
      <div class="chat-message ai">
        <div class="message-inner">
          <div class="message-avatar">B</div>
          <div class="message-bubble">
            <div class="typing-indicator">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      </div>`;
    }
    return html;
  }

  // Client-side fallback parser for historical messages stored with :::action blocks
  _parseClientActions(content, role) {
    if (role !== 'ai' || !content || content.indexOf(':::action') === -1) return [];
    var actions = [];
    var re = /:::action\s*\n([\s\S]*?)\n:::/g;
    var match;
    while ((match = re.exec(content)) !== null) {
      try {
        var parsed = JSON.parse(match[1].trim());
        if (parsed && parsed.type) actions.push(parsed);
      } catch (e) { /* skip malformed */ }
    }
    return actions;
  }

  renderActionBlock(action, msgId, idx) {
    var payload = encodeURIComponent(JSON.stringify(action));
    var doneKey = msgId + '-' + idx;
    var isDone = this.actionsDone && this.actionsDone[doneKey];

    if (action.type === 'content_drafts') {
      var items = action.items || [];
      var itemsHtml = '';
      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var editKey = msgId + '-' + idx + '-' + i;
        var isEditing = this.actionEdits && this.actionEdits[editKey] !== undefined;
        var displayContent = (this.actionEdits && this.actionEdits[editKey]) || item.content || '';
        var preview = displayContent.length > 180 ? displayContent.substring(0, 180) + '...' : displayContent;
        itemsHtml += '<div class="action-draft-item" data-item-idx="' + i + '">'
          + '<div class="action-draft-meta"><span class="action-platform-badge plat-' + escapeHTML(item.platform) + '">' + escapeHTML(item.platform) + '</span>'
          + (item.tone ? '<span class="action-tone-tag">' + escapeHTML(item.tone) + '</span>' : '') + '</div>'
          + (isEditing
            ? '<textarea class="action-draft-editor" data-action="action-edit-save" data-msg-id="' + msgId + '" data-action-idx="' + idx + '" data-item-idx="' + i + '">' + escapeHTML(displayContent) + '</textarea>'
            : '<div class="action-draft-preview">' + escapeHTML(preview) + '</div>')
          + '<div class="action-draft-controls">'
          + (isEditing ? '' : '<button class="btn-msg-action" data-action="action-edit-draft" data-msg-id="' + msgId + '" data-action-idx="' + idx + '" data-item-idx="' + i + '">Edit</button>')
          + '<button class="btn-msg-action" data-action="action-remove-draft" data-msg-id="' + msgId + '" data-action-idx="' + idx + '" data-item-idx="' + i + '">Remove</button>'
          + '</div></div>';
      }
      return '<div class="action-block' + (isDone ? ' action-block--done' : '') + '" data-msg-id="' + msgId + '" data-action-idx="' + idx + '">'
        + '<div class="action-block-header"><svg class="action-block-icon" width="20" height="20" viewBox="0 0 24 24"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>' + escapeHTML(action.label || 'Content Drafts') + '</div>'
        + '<div class="action-block-items">' + itemsHtml + '</div>'
        + (isDone ? '<div class="action-block-success">&#10003; Saved to Library</div>' : '<div class="action-block-actions">'
          + '<button class="btn btn-primary btn-sm" data-action="action-save-library" data-payload="' + payload + '">Save All to Library</button>'
          + '<button class="btn btn-secondary btn-sm" data-action="action-schedule-week" data-payload="' + payload + '">Schedule This Week</button>'
          + '</div>')
        + '</div>';
    }

    if (action.type === 'campaign_plan') {
      var c = action.campaign || {};
      var platBadges = '';
      var plats = c.platforms || [];
      for (var pi = 0; pi < plats.length; pi++) {
        var pLabel = plats[pi] === 'x' ? 'X' : plats[pi].charAt(0).toUpperCase() + plats[pi].slice(1);
        platBadges += '<span class="chat-platform-badge">' + escapeHTML(pLabel) + '</span>';
      }
      return '<div class="action-block' + (isDone ? ' action-block--done' : '') + '" data-msg-id="' + msgId + '" data-action-idx="' + idx + '">'
        + '<div class="action-block-header"><svg class="action-block-icon" width="20" height="20" viewBox="0 0 24 24"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>' + escapeHTML(action.label || 'Campaign Plan') + '</div>'
        + '<div class="action-campaign-card">'
        + '<div class="action-campaign-row"><strong>Name:</strong> ' + escapeHTML(c.name || '') + '</div>'
        + '<div class="action-campaign-row"><strong>Goal:</strong> ' + escapeHTML(c.goal || '') + '</div>'
        + '<div class="action-campaign-row"><strong>Platforms:</strong> ' + platBadges + '</div>'
        + '<div class="action-campaign-row"><strong>Duration:</strong> ' + (c.totalItems || 0) + ' posts over ' + (c.days || 0) + ' days</div>'
        + (c.tone ? '<div class="action-campaign-row"><strong>Tone:</strong> ' + escapeHTML(c.tone) + '</div>' : '')
        + '</div>'
        + (isDone ? '<div class="action-block-success">&#10003; Campaign Created</div>' : '<div class="action-block-actions">'
          + '<button class="btn btn-primary btn-sm" data-action="action-create-campaign" data-payload="' + payload + '">Create This Campaign</button>'
          + '</div>')
        + '</div>';
    }

    if (action.type === 'generate_themes') {
      var themes = action.themes || [];
      var themesHtml = '';
      for (var ti = 0; ti < themes.length; ti++) {
        var t = themes[ti];
        var tplatBadges = '';
        var tplats = t.platforms || [];
        for (var tpi = 0; tpi < tplats.length; tpi++) {
          tplatBadges += '<span class="action-platform-badge plat-' + escapeHTML(tplats[tpi]) + '">' + escapeHTML(tplats[tpi]) + '</span>';
        }
        themesHtml += '<div class="action-theme-row">'
          + '<div class="action-theme-info"><div class="action-theme-title">' + escapeHTML(t.title || '') + '</div>'
          + '<div class="action-theme-desc">' + escapeHTML(t.description || '') + '</div>'
          + '<div class="action-theme-plats">' + tplatBadges + '</div></div>'
          + '<button class="btn btn-secondary btn-sm" data-action="action-generate-theme" data-payload="' + encodeURIComponent(JSON.stringify(t)) + '">Generate Posts</button>'
          + '</div>';
      }
      return '<div class="action-block" data-msg-id="' + msgId + '" data-action-idx="' + idx + '">'
        + '<div class="action-block-header"><svg class="action-block-icon" width="20" height="20" viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>' + escapeHTML(action.label || 'Content Themes') + '</div>'
        + '<div class="action-block-items">' + themesHtml + '</div>'
        + '</div>';
    }

    if (action.type === 'save_insight') {
      return '<div class="action-block' + (isDone ? ' action-block--done' : '') + '" data-msg-id="' + msgId + '" data-action-idx="' + idx + '">'
        + '<div class="action-block-header"><svg class="action-block-icon" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>' + escapeHTML(action.label || 'Save Insight') + '</div>'
        + '<div class="action-insight-card">' + escapeHTML(action.content || '') + '</div>'
        + (isDone ? '<div class="action-block-success">&#10003; Saved to Brain</div>' : '<div class="action-block-actions">'
          + '<button class="btn btn-primary btn-sm" data-action="action-save-insight" data-payload="' + payload + '">Save to Brain</button>'
          + '</div>')
        + '</div>';
    }

    if (action.type === 'topic_edit') {
      // Topic edits are an autopilot-only feature. Hide the entire block from
      // non-admin users until autopilot ships.
      const role = (window.__auth__ && window.__auth__.role) || '';
      if (role !== 'god_admin') return '';

      const topicId = action.topic_id || action.topicId || '';
      const hasTitle = typeof action.title === 'string' && action.title.trim();
      const hasHook  = typeof action.hook === 'string';
      const hasKw    = Array.isArray(action.keywords);
      const rows = [];
      if (hasTitle) rows.push({ label: 'Title', val: action.title });
      if (hasHook)  rows.push({ label: 'Hook',  val: action.hook });
      if (hasKw)    rows.push({ label: 'Keywords', val: action.keywords.join(', ') });
      if (!rows.length) return ''; // nothing to show

      const payload = encodeURIComponent(JSON.stringify({ topicId, title: action.title, hook: action.hook, keywords: action.keywords }));
      const rowsHtml = rows.map(r => '<div class="action-topic-row"><span class="action-topic-label">' + r.label + '</span><span class="action-topic-val">' + escapeHTML(r.val || '') + '</span></div>').join('');
      return '<div class="action-block' + (isDone ? ' action-block--done' : '') + '" data-msg-id="' + msgId + '" data-action-idx="' + idx + '">'
        + '<div class="action-block-header"><svg class="action-block-icon" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>Proposed topic edit</div>'
        + '<div class="action-topic-card">' + rowsHtml + '</div>'
        + (isDone ? '<div class="action-block-success">&#10003; Applied to autopilot</div>' : '<div class="action-block-actions">'
          + '<button class="btn btn-primary btn-sm" data-action="action-apply-topic" data-payload="' + payload + '">Apply to autopilot</button>'
          + '<button class="btn btn-primary btn-sm" data-action="action-apply-topic-return" data-payload="' + payload + '">Apply &amp; return to autopilot</button>'
          + '<button class="btn btn-ghost btn-sm" data-action="action-topic-to-library" data-payload="' + payload + '">Save a copy to Library</button>'
          + '<button class="btn btn-ghost btn-sm" data-action="action-topic-discard" data-payload="' + payload + '">Discard</button>'
          + '</div>')
        + '</div>';
    }

    return ''; // Unknown type
  }

  renderFacts() {
    const { facts } = this.memoryData;
    const all = [
      ...facts.goals.map(f => ({ ...f, typeLabel: 'Goal' })),
      ...facts.offers.map(f => ({ ...f, typeLabel: 'Offer' })),
      ...facts.rules_do.map(f => ({ ...f, typeLabel: 'Rule (Do)' })),
      ...facts.rules_dont.map(f => ({ ...f, typeLabel: 'Rule (Don\'t)' })),
      ...(facts.other || []).map(f => ({ ...f, typeLabel: 'Other' }))
    ];

    if (all.length === 0) return `<div class="empty-state">No specific facts found. Add your first truth above.</div>`;

    return all.map(f => `
      <div class="fact-item" data-id="${f.id}">
        <div class="fact-meta">
          <span class="badge ${f.fact_type}">${escapeHTML(f.typeLabel)}</span>
          <span class="date">${formatDateMDY(f.created_at)}</span>
        </div>
        <div class="fact-content">${escapeHTML(f.content)}</div>
        <button class="btn-delete" data-action="delete-fact" data-id="${f.id}" title="Delete" aria-label="Delete">${svgIcon('trash', 14, 14)}</button>
      </div>
    `).join('');
  }

  renderDocuments() {
    const { documents } = this.memoryData;
    if (!documents || documents.length === 0) {
      return `<tr><td colspan="5" class="empty-state">No documents yet. Drop in a pitch deck, style guide, or product sheet above — the Brain reads them and writes with that knowledge.</td></tr>`;
    }

    return documents.map(doc => `
      <tr class="vault-item" data-id="${doc.id}" data-testid="brain-vault-item">
        <td><strong>${escapeHTML(doc.filename)}</strong>${doc.source_type === 'google_docs' ? '<span class="vault-source-badge vault-source-badge--google">Google Docs</span>' : doc.source_type === 'notion' ? '<span class="vault-source-badge vault-source-badge--notion">Notion</span>' : doc.source_type === 'url_scan' ? '<span class="vault-source-badge vault-source-badge--url">Web Scan</span>' : ''}</td>
        <td><span class="file-type-badge">${escapeHTML(doc.file_type)}</span></td>
        <td>${this.formatSize(doc.file_size)}</td>
        <td>${formatDateMDY(doc.uploaded_at)}</td>
        <td class="analytics-td--right">
          <div class="action-group" style="justify-content: flex-end;">
            <a href="/api/memory/documents/${doc.id}/download" class="btn-icon" title="Download" aria-label="Download" data-testid="brain-vault-download"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></a>
            <button class="btn-icon btn-delete" data-action="delete-doc" data-id="${doc.id}" title="Delete" aria-label="Delete">${svgIcon('trash', 14, 14)}</button>
          </div>
        </td>
      </tr>
    `).join('');
  }

  formatSize(bytes) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  truncateText(text, maxLen) {
    if (!text) return '';
    const clean = text.replace(/\n/g, ' ').trim();
    return clean.length > maxLen ? clean.substring(0, maxLen) + '...' : clean;
  }

  bindEvents() {
    if (!this._eventsBound) {
      this.container.addEventListener("click", (e) => this.handleGlobalClick(e));
      this._eventsBound = true;
    }

    // Input handlers attached per render
    if (this.activeTab === 'brand') {
      // Quick Start handlers
      // Quick Start: toggle custom voice input on Other
      const qsVoiceSelect = this.container.querySelector('#qs-voice');
      const qsVoiceCustom = this.container.querySelector('#qs-voice-custom');
      if (qsVoiceSelect && qsVoiceCustom) {
        qsVoiceSelect.addEventListener('change', () => {
          qsVoiceCustom.style.display = qsVoiceSelect.value === 'Other' ? 'block' : 'none';
        });
      }

      const qsSaveBtn = this.container.querySelector('#qs-save-btn');
      qsSaveBtn?.addEventListener('click', async () => {
        const name = this.container.querySelector('#qs-brand-name')?.value?.trim() || '';
        const audience = this.container.querySelector('#qs-audience')?.value?.trim() || '';
        const voiceRaw = this.container.querySelector('#qs-voice')?.value || '';
        const voiceCustom = this.container.querySelector('#qs-voice-custom')?.value?.trim() || '';
        const voice = voiceRaw === 'Other' ? (voiceCustom || 'Professional') : voiceRaw;
        if (!name && !audience && !voice) {
          toast('error', 'Fill in at least one field to get started.');
          return;
        }
        const profile = this.memoryData?.profile || {};
        const payload = {
          mission: name ? `${name} — ${profile.mission || ''}`.trim().replace(/ — $/, '') : (profile.mission || ''),
          audience: audience || (profile.audience || ''),
          voice: voice || (profile.voice || ''),
          offers_summary: profile.offers_summary || '',
          cta: profile.cta || '',
          keywords: profile.keywords || '',
          avoid_words: profile.avoid_words || ''
        };
        try {
          qsSaveBtn.disabled = true;
          qsSaveBtn.textContent = 'Saving...';
          await client.updateProfile(payload);
          sessionStorage.setItem('brain.quickStartDismissed', 'true');
          await this.refresh();
          toast('success', 'Brain initialized! Fill in more details below.');
        } catch (err) {
          toast('error', 'Failed to save: ' + err.message);
          qsSaveBtn.disabled = false;
          qsSaveBtn.textContent = 'Save & Continue';
        }
      });
      const qsSkipBtn = this.container.querySelector('#qs-skip-btn');
      qsSkipBtn?.addEventListener('click', () => {
        sessionStorage.setItem('brain.quickStartDismissed', 'true');
        const card = this.container.querySelector('.brain-quickstart');
        if (card) card.remove();
      });

      const saveBtn = this.container.querySelector("#save-profile");
      saveBtn?.addEventListener("click", () => this.handleSaveProfile());

      // URL Scanner toggle + scan
      const scannerToggle = this.container.querySelector('#brain-scanner-toggle');
      const scannerBody = this.container.querySelector('#brain-scanner-body');
      scannerToggle?.addEventListener('click', () => {
        scannerBody?.classList.toggle('hide');
        const chevron = scannerToggle.querySelector('.brain-url-scanner__chevron');
        if (chevron) chevron.style.transform = scannerBody?.classList.contains('hide') ? '' : 'rotate(180deg)';
      });

      const scannerBtn = this.container.querySelector('#brain-scanner-btn');
      scannerBtn?.addEventListener('click', async () => {
        const urlInput = this.container.querySelector('#brain-scanner-url');
        const statusEl = this.container.querySelector('#brain-scanner-status');
        const resultsEl = this.container.querySelector('#brain-scanner-results');
        let url = urlInput?.value?.trim();
        if (!url) { statusEl.textContent = 'Enter a URL to scan.'; statusEl.style.color = 'var(--danger)'; return; }
        if (!url.startsWith('http')) url = 'https://' + url;

        scannerBtn.disabled = true;
        scannerBtn.textContent = 'Scanning...';
        statusEl.textContent = '';
        statusEl.style.color = '';
        resultsEl?.classList.add('hide');

        try {
          const res = await client.suggestProfile(url);
          const s = res.suggestions;
          scannerBtn.disabled = false;
          scannerBtn.textContent = 'Scan & Suggest';
          this._scanSuggestions = s;

          resultsEl.innerHTML = `
            <div class="brain-url-scanner__preview">
              ${res.warning ? `<div style="padding:8px 12px;margin-bottom:12px;border-radius:8px;background:rgba(245,158,11,0.1);border:1px solid rgba(245,158,11,0.2);color:#F59E0B;font-size:13px;">${escapeHtml(res.warning)}</div>` : ''}
              <div class="brain-url-scanner__preview-title">Suggestions from <strong>${new URL(url).hostname}</strong></div>
              ${s.mission ? `<div class="brain-url-scanner__item"><strong>Mission:</strong> ${escapeHtml(s.mission).substring(0, 120)}</div>` : ''}
              ${s.audience ? `<div class="brain-url-scanner__item"><strong>Audience:</strong> ${escapeHtml(s.audience).substring(0, 120)}</div>` : ''}
              ${s.voice ? `<div class="brain-url-scanner__item"><strong>Voice:</strong> ${escapeHtml(s.voice)}</div>` : ''}
              ${s.offers ? `<div class="brain-url-scanner__item"><strong>Offers:</strong> ${escapeHtml(s.offers).substring(0, 120)}</div>` : ''}
              ${s.keywords ? `<div class="brain-url-scanner__item"><strong>Keywords:</strong> ${escapeHtml(s.keywords)}</div>` : ''}
              <div style="display:flex;gap:8px;margin-top:12px;flex-wrap:wrap;">
                <button id="brain-scanner-apply" class="btn btn-primary btn-sm">Apply Suggestions</button>
                <button id="brain-scanner-merge" class="btn btn-glass btn-sm">Merge (keep existing)</button>
                <button id="brain-scanner-connect" class="btn btn-glass btn-sm" title="Keep this site as a persistent brain source without overwriting fields">Stay Connected</button>
              </div>
            </div>
          `;
          resultsEl.classList.remove('hide');

          const captureSnapshot = () => {
            const f = (id) => (this.container.querySelector(id)?.value || '');
            this._profileSnapshot = {
              mission: f('#profile-mission'), audience: f('#profile-audience'),
              voice: f('#profile-voice'), offers: f('#profile-offers'),
              cta: f('#profile-cta'), keywords: f('#profile-keywords'),
              avoid: f('#profile-avoid')
            };
          };
          const showUndo = () => {
            statusEl.innerHTML += ' <button id="brain-scanner-undo" class="brain-undo-btn" title="Undo scan changes">&#x21BA; Undo</button>';
            statusEl.querySelector('#brain-scanner-undo')?.addEventListener('click', () => {
              if (!this._profileSnapshot) return;
              const s = this._profileSnapshot;
              this.container.querySelector('#profile-mission').value = s.mission;
              this.container.querySelector('#profile-audience').value = s.audience;
              this.container.querySelector('#profile-voice').value = s.voice;
              this.container.querySelector('#profile-offers').value = s.offers;
              this.container.querySelector('#profile-cta').value = s.cta;
              this.container.querySelector('#profile-keywords').value = s.keywords;
              this.container.querySelector('#profile-avoid').value = s.avoid;
              statusEl.textContent = 'Changes undone. Fields restored to previous values.';
              statusEl.style.color = 'var(--text-muted)';
              this._profileSnapshot = null;
            });
          };

          resultsEl.querySelector('#brain-scanner-apply')?.addEventListener('click', async () => {
            captureSnapshot();
            const sg = this._scanSuggestions;
            if (sg.mission) this.container.querySelector('#profile-mission').value = sg.mission;
            if (sg.audience) this.container.querySelector('#profile-audience').value = sg.audience;
            if (sg.voice) this.container.querySelector('#profile-voice').value = sg.voice;
            if (sg.offers) this.container.querySelector('#profile-offers').value = sg.offers;
            if (sg.cta) this.container.querySelector('#profile-cta').value = sg.cta;
            if (sg.keywords) this.container.querySelector('#profile-keywords').value = sg.keywords;
            if (sg.avoid_words) this.container.querySelector('#profile-avoid').value = sg.avoid_words;
            resultsEl.classList.add('hide');
            // Auto-save so changes persist without requiring a second click
            statusEl.textContent = 'Saving suggestions...';
            statusEl.style.color = 'var(--text-muted)';
            try {
              await this.handleSaveProfile();
              statusEl.textContent = 'Suggestions applied and saved!';
              statusEl.style.color = 'var(--success)';
            } catch {
              statusEl.textContent = 'Suggestions applied but save failed. Click Save Identity to retry.';
              statusEl.style.color = 'var(--danger)';
            }
            showUndo();
          });

          resultsEl.querySelector('#brain-scanner-merge')?.addEventListener('click', async () => {
            captureSnapshot();
            const sg = this._scanSuggestions;
            const mergeField = (id, val) => { const el = this.container.querySelector(id); if (el && val && !el.value.trim()) el.value = val; };
            mergeField('#profile-mission', sg.mission);
            mergeField('#profile-audience', sg.audience);
            mergeField('#profile-voice', sg.voice);
            mergeField('#profile-offers', sg.offers);
            mergeField('#profile-cta', sg.cta);
            mergeField('#profile-keywords', sg.keywords);
            mergeField('#profile-avoid', sg.avoid_words);
            resultsEl.classList.add('hide');
            // Auto-save merged fields
            statusEl.textContent = 'Saving merged fields...';
            statusEl.style.color = 'var(--text-muted)';
            try {
              await this.handleSaveProfile();
              statusEl.textContent = 'Merged and saved!';
              statusEl.style.color = 'var(--success)';
            } catch {
              statusEl.textContent = 'Merged but save failed. Click Save Identity to retry.';
              statusEl.style.color = 'var(--danger)';
            }
            showUndo();
          });

          resultsEl.querySelector('#brain-scanner-connect')?.addEventListener('click', async () => {
            const urlInput = this.container.querySelector('#brain-scanner-url');
            const connectUrl = urlInput?.value?.trim();
            if (!connectUrl) return;
            try {
              resultsEl.classList.add('hide');
              statusEl.textContent = 'Connecting website as brain source...';
              statusEl.style.color = 'var(--text-muted)';
              const res = await client.connectUrl(connectUrl);
              statusEl.textContent = `Connected ${res.hostname || 'website'} as a persistent brain source.`;
              statusEl.style.color = 'var(--success)';
            } catch (err) {
              statusEl.textContent = err.message || 'Failed to connect website';
              statusEl.style.color = 'var(--danger)';
            }
          });
        } catch (err) {
          scannerBtn.disabled = false;
          scannerBtn.textContent = 'Scan & Suggest';
          statusEl.textContent = err.message || 'Scan failed. Check the URL and try again.';
          statusEl.style.color = 'var(--danger)';
        }
      });

      const addFactBtn = this.container.querySelector("#add-fact");
      addFactBtn?.addEventListener("click", () => this.handleAddFact());

      const testBrainBtn = this.container.querySelector('[data-action="test-brain"]');
      testBrainBtn?.addEventListener("click", () => this.setActiveTab('chat'));

      // Persona handlers
      const addPersonaBtn = this.container.querySelector('#add-persona-btn');
      const personaForm = this.container.querySelector('#persona-form');
      const personaCancelBtn = this.container.querySelector('#persona-cancel-btn');
      const personaSaveBtn = this.container.querySelector('#persona-save-btn');

      addPersonaBtn?.addEventListener('click', () => { if (personaForm) personaForm.style.display = 'block'; });
      personaCancelBtn?.addEventListener('click', () => { if (personaForm) personaForm.style.display = 'none'; });
      personaSaveBtn?.addEventListener('click', async () => {
        const name = this.container.querySelector('#persona-name')?.value?.trim();
        if (!name) return;
        try {
          await client.addPersona({
            name,
            demographics: this.container.querySelector('#persona-demographics')?.value?.trim() || '',
            pain_points: this.container.querySelector('#persona-pain-points')?.value?.trim() || '',
            goals: this.container.querySelector('#persona-goals')?.value?.trim() || '',
            language_style: this.container.querySelector('#persona-language')?.value?.trim() || '',
            platforms: this.container.querySelector('#persona-platforms')?.value?.trim() || '',
          });
          await this.refresh();
        } catch(e) { console.error('Failed to add persona:', e); toast('error', 'Couldn\'t add that persona — please try again.'); }
      });

      this.container.querySelectorAll('[data-action="delete-persona"]').forEach(btn => {
        btn.addEventListener('click', async () => {
          if (!confirm('Delete this persona?')) return;
          try { await client.deletePersona(btn.dataset.id); await this.refresh(); } catch(e) { console.error(e); toast('error', 'Couldn\'t delete that persona — please try again.'); }
        });
      });
    }

    if (this.activeTab === 'documents') {
      const uploadZone = this.container.querySelector("#vault-upload-zone");
      const fileInput = this.container.querySelector("#brain-vault-file-input");
      const uploadBtn = this.container.querySelector("#brain-vault-upload-btn");

      uploadBtn?.addEventListener("click", () => fileInput?.click());
      fileInput?.addEventListener("change", (e) => this.handleFileChange(e));

      uploadZone?.addEventListener("dragover", (e) => {
        e.preventDefault();
        uploadZone.classList.add("dragover");
      });

      uploadZone?.addEventListener("dragleave", () => {
        uploadZone.classList.remove("dragover");
      });

      uploadZone?.addEventListener("drop", (e) => {
        e.preventDefault();
        uploadZone.classList.remove("dragover");
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          this.handleFileUpload(files[0]);
        }
      });

      // URL Scanner
      const scanBtn = this.container.querySelector('#vault-scan-url-btn');
      if (scanBtn) {
        scanBtn.addEventListener('click', async () => {
          const input = this.container.querySelector('#vault-scan-url-input');
          const status = this.container.querySelector('#vault-scan-status');
          let url = input?.value?.trim();
          if (!url) return;
          if (!url.startsWith('http')) url = 'https://' + url;
          scanBtn.disabled = true;
          const origText = scanBtn.innerHTML;
          scanBtn.textContent = 'Scanning...';
          if (status) status.textContent = '';
          try {
            const res = await client.scanUrl(url);
            if (status) status.innerHTML = `<span style="color:var(--success)">Scanned ${res.charCount.toLocaleString()} characters from ${escapeHTML(res.filename)}</span>`;
            input.value = '';
            await this.loadMemory();
            this.render();
          } catch (err) {
            if (status) status.innerHTML = `<span style="color:var(--danger)">${escapeHTML(err.message || 'Failed to scan URL')}</span>`;
          } finally {
            scanBtn.disabled = false;
            scanBtn.innerHTML = origText;
          }
        });
      }

      // Connected website source actions
      this.container.querySelectorAll('[data-action="rescan-source"]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.sourceId;
          btn.textContent = '...';
          btn.disabled = true;
          try {
            const res = await client.rescanSource(id);
            btn.textContent = res.changed ? '\u2713' : '\u21BB';
            setTimeout(() => { btn.textContent = '\u21BB'; btn.disabled = false; }, 2000);
          } catch (e) { btn.textContent = '!'; btn.disabled = false; }
        });
      });
      this.container.querySelectorAll('[data-action="disconnect-source"]').forEach(btn => {
        btn.addEventListener('click', async () => {
          if (!confirm('Disconnect this website source?')) return;
          try {
            await client.disconnectSource(btn.dataset.sourceId);
            await this.loadMemory();
            this.render();
          } catch (e) { alert(e.message || 'Failed to disconnect'); }
        });
      });
    }

    if (this.activeTab === 'chat') {
      const chatInput = this.container.querySelector("#chat-input");
      const chatSend = this.container.querySelector("#chat-send");
      const projectSelect = this.container.querySelector("#project-select");
      const newChatBtn = this.container.querySelector("#new-chat-btn");
      const newProjectBtn = this.container.querySelector("#new-project-btn");
      const memoryToggleBtn = this.container.querySelector("#memory-toggle-btn");
      const panelAddFactBtn = this.container.querySelector("#panel-add-fact");
      const panelFactContent = this.container.querySelector("#panel-fact-content");
      const searchInput = this.container.querySelector("#chat-search-input");

      chatSend?.addEventListener("click", () => this.handleChat());
      chatInput?.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          this.handleChat();
        }
      });

      // Suggestion prompt cards
      var suggestionBtns = this.container.querySelectorAll('.brain-chat-suggestion');
      for (var si = 0; si < suggestionBtns.length; si++) {
        suggestionBtns[si].addEventListener('click', (e) => {
          var prompt = e.currentTarget.getAttribute('data-prompt');
          if (chatInput && prompt) {
            chatInput.value = prompt;
            this.handleChat();
          }
        });
      }

      // Phase 7 — pre-fill the composer from ?prompt=... deep-link.
      // Used by empty-state "Talk to Heist" CTAs on calendar / library /
      // campaigns. We pre-fill but DO NOT auto-send — the user should see
      // their prompt and edit it before hitting send. Consume once to
      // avoid re-triggering on subsequent renders.
      if (this._deepLinkPrompt && chatInput) {
        try {
          chatInput.value = this._deepLinkPrompt;
          chatInput.focus();
        } catch (_) { /* best-effort */ }
        this._deepLinkPrompt = null;
      }

      projectSelect?.addEventListener("change", (e) => this.handleSelectProject(e.target.value));
      newChatBtn?.addEventListener("click", () => this.handleNewChat());
      newProjectBtn?.addEventListener("click", () => this.showNewProjectModal());
      memoryToggleBtn?.addEventListener("click", () => this.toggleMemoryPanel());

      var sidebarToggle = this.container.querySelector('#chat-sidebar-toggle');
      if (sidebarToggle) sidebarToggle.addEventListener('click', () => {
        this.isChatSidebarOpen = !this.isChatSidebarOpen;
        localStorage.setItem('brain.chatSidebarOpen', this.isChatSidebarOpen ? 'true' : 'false');
        this.render();
      });
      panelAddFactBtn?.addEventListener("click", () => this.handlePanelAddFact());
      panelFactContent?.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          this.handlePanelAddFact();
        }
      });

      searchInput?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.runChatSearch(e.target.value);
        }
      });
    }
  }

  handleGlobalClick(e) {
    const tabBtn = e.target.closest('[data-tab]');
    if (tabBtn) {
      this.setActiveTab(tabBtn.dataset.tab);
      return;
    }

    const deleteFactBtn = e.target.closest('.btn-delete-fact') || e.target.closest('[data-action="delete-fact"]');
    if (deleteFactBtn) {
      this.handleDeleteFact(deleteFactBtn.dataset.id);
      return;
    }

    const docBtn = e.target.closest('[data-action="delete-doc"]');
    if (docBtn) {
      this.handleDeleteDoc(docBtn.dataset.id);
      return;
    }

    // Doc picker buttons
    if (e.target.closest('[data-action="pick-google-docs"]')) {
      openDocPicker('google_docs', { onComplete: () => this.refresh() });
      return;
    }
    if (e.target.closest('[data-action="pick-notion-pages"]')) {
      openDocPicker('notion', { onComplete: () => this.refresh() });
      return;
    }

    // Media library drawer
    if (e.target.closest('#brain-open-media-library')) {
      openMediaLibrary(this);
      return;
    }

    // Vault trash handlers
    if (e.target.closest('#vault-trash-toggle')) {
      this.showVaultTrash = !this.showVaultTrash;
      if (this.showVaultTrash && !this.vaultTrash) {
        client.getDocumentTrash().then(res => { this.vaultTrash = res.documents || []; this.render(); }).catch(() => { this.vaultTrash = []; this.render(); });
      } else { this.render(); }
      return;
    }
    if (e.target.closest('#vault-empty-trash')) {
      if (confirm('Permanently delete all items in trash? This cannot be undone.')) {
        client.emptyDocumentTrash().then(() => { this.vaultTrash = []; this.render(); }).catch(err => console.error(err));
      }
      return;
    }
    const restoreBtn = e.target.closest('[data-action="restore-doc"]');
    if (restoreBtn) {
      client.restoreDocument(restoreBtn.dataset.id).then(() => this.refresh()).catch(err => console.error(err));
      return;
    }
    const permDeleteBtn = e.target.closest('[data-action="perm-delete-doc"]');
    if (permDeleteBtn) {
      if (confirm('Permanently delete this document? This cannot be undone.')) {
        client.permanentDeleteDocument(permDeleteBtn.dataset.id).then(() => {
          this.vaultTrash = (this.vaultTrash || []).filter(d => d.id !== permDeleteBtn.dataset.id);
          this.render();
        }).catch(err => console.error(err));
      }
      return;
    }

    const mediaDeleteBtn = e.target.closest('[data-action="delete-workspace-media"]');
    if (mediaDeleteBtn) {
      const mediaId = mediaDeleteBtn.dataset.mediaId;
      if (mediaId && confirm('Delete this media file?')) {
        client.deleteWorkspaceMedia(mediaId).then(() => this.refresh()).catch(err => console.error('[MEDIA_DELETE]', err));
      }
      return;
    }

    // Phase Q.4A Chat Handlers
    const threadItem = e.target.closest('.thread-item');
    if (threadItem && !e.target.closest('.thread-actions')) {
      this.handleSelectThread(threadItem.dataset.threadid);
      return;
    }

    const renameThreadBtn = e.target.closest('[data-action="rename-thread"]');
    if (renameThreadBtn) {
      this.handleRenameThread(renameThreadBtn.dataset.id);
      return;
    }

    const deleteThreadBtn = e.target.closest('[data-action="delete-thread"]');
    if (deleteThreadBtn) {
      this.handleDeleteThread(deleteThreadBtn.dataset.id);
      return;
    }

    // Phase Q.4C Handlers
    const actionsBtn = e.target.closest('#chat-actions-btn');
    if (actionsBtn) {
      this.showChatActions = !this.showChatActions;
      this.render();
      return;
    } else {
      if (this.showChatActions) {
        this.showChatActions = false;
        this.render();
      }
    }

    const exportThreadMd = e.target.closest('[data-action="export-thread-md"]');
    if (exportThreadMd) { this.handleExport('thread', 'md'); return; }
    const exportThreadJson = e.target.closest('[data-action="export-thread-json"]');
    if (exportThreadJson) { this.handleExport('thread', 'json'); return; }
    const exportProjectMd = e.target.closest('[data-action="export-project-md"]');
    if (exportProjectMd) { this.handleExport('project', 'md'); return; }
    const exportProjectJson = e.target.closest('[data-action="export-project-json"]');
    if (exportProjectJson) { this.handleExport('project', 'json'); return; }

    const shareBtn = e.target.closest('[data-action="share-thread"]');
    if (shareBtn) { this.handleShare(); return; }

    const closeShareBtn = e.target.closest('#close-share-modal');
    if (closeShareBtn) { this.shareModalOpen = false; this.render(); return; }

    const revokeShareBtn = e.target.closest('[data-action="revoke-share"]');
    if (revokeShareBtn) { this.handleRevokeShare(revokeShareBtn.dataset.id); return; }

    const copyBtn = e.target.closest('.copy-btn');
    if (copyBtn) {
      const url = copyBtn.dataset.url;
      navigator.clipboard.writeText(window.location.origin + url);
      copyBtn.innerText = "Copied!";
      setTimeout(() => {
        const btn = this.container.querySelector(`.copy-btn[data-url="${url}"]`);
        if (btn) btn.innerText = "Copy";
      }, 2000);
      return;
    }

    const copyMsgBtn = e.target.closest('[data-action="copy-msg"]');
    if (copyMsgBtn) {
      navigator.clipboard.writeText(decodeURIComponent(copyMsgBtn.dataset.content));
      copyMsgBtn.textContent = "Copied!";
      setTimeout(() => copyMsgBtn.textContent = "Copy", 2000);
      return;
    }

    const saveMsgBtn = e.target.closest('[data-action="save-to-brain"]');
    if (saveMsgBtn) {
      this.showSaveToBrainModal(decodeURIComponent(saveMsgBtn.dataset.content));
      return;
    }

    // G10-2: Quick save to brain from hover button
    const chatSaveQuick = e.target.closest('[data-action="chat-save-quick"]');
    if (chatSaveQuick) {
      this.handleChatSaveQuick(chatSaveQuick);
      return;
    }

    // --- Actionable Brain Chat handlers ---
    const actionSaveLibrary = e.target.closest('[data-action="action-save-library"]');
    if (actionSaveLibrary) {
      this._handleActionSaveLibrary(actionSaveLibrary);
      return;
    }
    const actionScheduleWeek = e.target.closest('[data-action="action-schedule-week"]');
    if (actionScheduleWeek) {
      this._handleActionScheduleWeek(actionScheduleWeek);
      return;
    }
    const actionCreateCampaign = e.target.closest('[data-action="action-create-campaign"]');
    if (actionCreateCampaign) {
      this._handleActionCreateCampaign(actionCreateCampaign);
      return;
    }
    const actionGenerateTheme = e.target.closest('[data-action="action-generate-theme"]');
    if (actionGenerateTheme) {
      this._handleActionGenerateTheme(actionGenerateTheme);
      return;
    }
    const actionSaveInsight = e.target.closest('[data-action="action-save-insight"]');
    if (actionSaveInsight) {
      this._handleActionSaveInsight(actionSaveInsight);
      return;
    }
    const actionEditDraft = e.target.closest('[data-action="action-edit-draft"]');
    if (actionEditDraft) {
      var draftItem = actionEditDraft.closest('.action-draft-item');
      if (!draftItem) return;
      var previewEl = draftItem.querySelector('.action-draft-preview');
      if (!previewEl) return;
      var currentText = previewEl.textContent || '';
      var textarea = document.createElement('textarea');
      textarea.className = 'action-draft-editor';
      textarea.value = currentText;
      textarea.dataset.msgId = actionEditDraft.dataset.msgId;
      textarea.dataset.actionIdx = actionEditDraft.dataset.actionIdx;
      textarea.dataset.itemIdx = actionEditDraft.dataset.itemIdx;
      previewEl.replaceWith(textarea);
      textarea.focus();
      // Replace Edit button with Save button
      actionEditDraft.textContent = 'Save';
      actionEditDraft.dataset.action = 'action-edit-save';
      // Store edit key for tracking
      var eKey = actionEditDraft.dataset.msgId + '-' + actionEditDraft.dataset.actionIdx + '-' + actionEditDraft.dataset.itemIdx;
      this.actionEdits[eKey] = currentText;
      return;
    }
    const actionEditSave = e.target.closest('[data-action="action-edit-save"]');
    if (actionEditSave) {
      var saveItem = actionEditSave.closest('.action-draft-item');
      if (!saveItem) return;
      var editorEl = saveItem.querySelector('.action-draft-editor');
      if (!editorEl) return;
      var newText = editorEl.value;
      var sKey = actionEditSave.dataset.msgId + '-' + actionEditSave.dataset.actionIdx + '-' + actionEditSave.dataset.itemIdx;
      this.actionEdits[sKey] = newText;
      // Replace textarea with preview
      var newPreview = document.createElement('div');
      newPreview.className = 'action-draft-preview';
      newPreview.textContent = newText.length > 180 ? newText.substring(0, 180) + '...' : newText;
      editorEl.replaceWith(newPreview);
      // Switch button back to Edit
      actionEditSave.textContent = 'Edit';
      actionEditSave.dataset.action = 'action-edit-draft';
      // Update the action in messages array if available
      var msg = this.messages.find(function(m) { return String(m.id) === actionEditSave.dataset.msgId; });
      if (msg && msg.actions) {
        var aIdx = parseInt(actionEditSave.dataset.actionIdx);
        var iIdx = parseInt(actionEditSave.dataset.itemIdx);
        if (msg.actions[aIdx] && msg.actions[aIdx].items && msg.actions[aIdx].items[iIdx]) {
          msg.actions[aIdx].items[iIdx].content = newText;
        }
      }
      toast('success', 'Draft updated');
      return;
    }
    const actionRemoveDraft = e.target.closest('[data-action="action-remove-draft"]');
    if (actionRemoveDraft) {
      var draftEl = actionRemoveDraft.closest('.action-draft-item');
      if (draftEl) {
        draftEl.style.transition = 'opacity 0.2s ease';
        draftEl.style.opacity = '0';
        setTimeout(function() { draftEl.remove(); }, 200);
      }
      // Also remove from messages array if available
      var rMsgId = actionRemoveDraft.dataset.msgId;
      var rIdx = parseInt(actionRemoveDraft.dataset.actionIdx);
      var rItem = parseInt(actionRemoveDraft.dataset.itemIdx);
      var rMsg = this.messages.find(function(m) { return String(m.id) === rMsgId; });
      if (rMsg && rMsg.actions && rMsg.actions[rIdx] && rMsg.actions[rIdx].items) {
        rMsg.actions[rIdx].items.splice(rItem, 1);
      }
      return;
    }

    const searchResult = e.target.closest('[data-testid="brain-search-result"]');
    if (searchResult) {
      this.handleSearchResultJump(searchResult.dataset.threadid, searchResult.dataset.messageid);
      return;
    }
  }

  async handleSelectProject(projectId) {
    try {
      this.activeProjectId = projectId;
      localStorage.setItem('brain.activeProjectId', projectId);

      // Load threads for this project
      const threadRes = await client.getChatThreads({ projectId });
      this.threads = threadRes.data || [];
      this.activeThreadId = this.threads.length > 0 ? this.threads[0].id : null;
      localStorage.setItem('brain.activeThreadId', this.activeThreadId);

      if (this.activeThreadId) {
        const msgRes = await client.getChatMessages(this.activeThreadId);
        this.messages = msgRes.data || [];
      } else {
        this.messages = [];
      }
      this.render();
    } catch (err) {
      console.error("[SELECT_PROJECT_ERROR]", err);
      toast('error', `Failed to load project threads: ${err.message} (RID: ${err.requestId || 'N/A'})`);
    }
  }

  showNewProjectModal() {
    const overlay = document.createElement('div');
    overlay.className = 'brain-chat-modal-overlay';
    overlay.innerHTML = `
      <div class="brain-chat-modal">
        <h3>New Project</h3>
        <div class="form-group">
          <label>Project Name</label>
          <input type="text" id="new-project-name" placeholder="e.g. Q1 Marketing" />
        </div>
        <div class="brain-chat-modal-actions">
          <button class="btn btn-secondary" id="cancel-project">Cancel</button>
          <button class="btn btn-primary" id="confirm-project">Create Project</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    overlay.querySelector('#new-project-name').focus();

    overlay.querySelector('#cancel-project').addEventListener('click', () => {
      document.body.removeChild(overlay);
    });

    overlay.querySelector('#confirm-project').addEventListener('click', async () => {
      const name = overlay.querySelector('#new-project-name').value.trim();
      if (!name) return;
      try {
        const res = await client.createChatProject({ name });
        document.body.removeChild(overlay);
        await this.loadChatState();
        this.handleSelectProject(res.id);
      } catch (err) {
        toast('error', `Failed to create project: ${err.message} (RID: ${err.requestId || 'N/A'})`);
      }
    });
  }

  toggleMemoryPanel() {
    this.isMemoryPanelOpen = !this.isMemoryPanelOpen;
    localStorage.setItem('brain.isMemoryPanelOpen', this.isMemoryPanelOpen);
    this.render();
  }

  async handlePanelAddFact() {
    const contentBtn = this.container.querySelector("#panel-add-fact");
    const contentArea = this.container.querySelector("#panel-fact-content");
    const typeSelect = this.container.querySelector("#panel-fact-type");

    const content = contentArea.value.trim();
    if (!content) return;

    try {
      contentBtn.disabled = true;
      await client.addFact({
        fact_type: typeSelect.value,
        content: content
      });
      contentArea.value = '';
      await this.refresh(); // Reload all memory
      this.showToast("Saved");
    } catch (err) {
      toast('error', `Failed to save fact: ${err.message}`);
    } finally {
      if (contentBtn) contentBtn.disabled = false;
    }
  }

  showToast(message) {
    this.showSavedToast = true;
    this.render();
    setTimeout(() => {
      this.showSavedToast = false;
      this.render();
    }, 2000);
  }

  async handleNewChat() {
    if (!this.activeProjectId) return;
    try {
      const res = await client.createChatThread({ projectId: this.activeProjectId });
      this.activeThreadId = res.id;
      localStorage.setItem('brain.activeThreadId', res.id);
      await this.loadChatState(); // Refresh all
      this.render();
    } catch (err) {
      toast('error', "Failed to create thread: " + err.message);
    }
  }

  async handleSelectThread(threadId) {
    this.activeThreadId = threadId;
    localStorage.setItem('brain.activeThreadId', threadId);
    try {
      const res = await client.getChatMessages(threadId);
      this.messages = res.data || [];
      this.render();
    } catch (err) {
      console.error("[SELECT_THREAD_ERROR]", err);
    }
  }

  async handleRenameThread(id) {
    const thread = this.threads.find(t => t.id === id);
    const newTitle = await promptModal({ title: 'Rename Thread', defaultValue: thread?.title || '', confirmLabel: 'Rename' });
    if (!newTitle || newTitle === thread?.title) return;

    try {
      await client.updateChatThread(id, { title: newTitle });
      await this.loadChatState();
      this.render();
    } catch (err) {
      toast('error', "Rename failed: " + err.message);
    }
  }

  async handleDeleteThread(id) {
    if (!await confirmModal({ title: 'Delete Thread', message: 'Delete this thread and all messages?', confirmLabel: 'Delete', danger: true })) return;
    try {
      await client.deleteThread(id);
      if (this.activeThreadId === id) this.activeThreadId = null;
      await this.loadChatState();
      this.render();
    } catch (err) {
      toast('error', "Delete failed: " + err.message);
    }
  }

  async handleChat() {
    const inputEl = this.container.querySelector("#chat-input");
    const message = inputEl.value.trim();

    if (!message || this.isSending) return;

    try {
      this.isSending = true;
      inputEl.value = "";

      // Auto-create thread if none active
      if (!this.activeThreadId) {
        if (!this.activeProjectId) {
          await this.loadChatState();
        }
        if (!this.activeProjectId) throw new Error("No active project found.");

        const res = await client.createChatThread({ projectId: this.activeProjectId });
        this.activeThreadId = res.id;
        localStorage.setItem('brain.activeThreadId', res.id);
        this.threads.unshift({ id: res.id, title: 'New chat', project_id: this.activeProjectId });
      }

      this.render();

      // Optimistic user message
      const tempUserMsg = { id: 'temp-user', role: 'user', content: message };
      this.messages.push(tempUserMsg);
      this.render();

      const response = await client.chatSend(this.activeThreadId, { message });

      if (response.ok) {
        this.messages = this.messages.filter(m => m.id !== 'temp-user');
        this.messages.push(response.user);
        var aiMsg = response.ai;
        if (aiMsg.actions && aiMsg.actions.length > 0) {
          // preserve parsed actions from server
        } else {
          // client-side fallback parse for historical/edge cases
          var parsed = this._parseClientActions(aiMsg.content, 'assistant');
          if (parsed.length > 0) { aiMsg.actions = parsed; }
        }
        this.messages.push(aiMsg);

        const currentThread = this.threads.find(t => t.id === this.activeThreadId);
        if (currentThread && currentThread.title === 'New chat') {
          const newTitle = message.substring(0, 40);
          await client.updateChatThread(this.activeThreadId, { title: newTitle });
          await this.loadChatState();
        }
      } else {
        throw response;
      }
    } catch (err) {
      console.error("[CHAT_ERROR]", err);
      toast('error', "Chat failed: " + (err.error?.message || err.message));
    } finally {
      this.isSending = false;
      this.render();
    }
  }

  // G10-2: Quick save AI message to Brain as insight
  async handleChatSaveQuick(btn) {
    const content = decodeURIComponent(btn.dataset.content).substring(0, 500);
    try {
      await client.addFact({ fact_type: 'insight', content });
      btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
      btn.style.opacity = '1';
      btn.style.color = 'var(--success, #22c55e)';
      toast('success', 'Saved to Brain');
      setTimeout(() => {
        btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>';
        btn.style.opacity = '';
        btn.style.color = '';
      }, 2000);
    } catch (err) {
      toast('error', 'Failed to save: ' + (err.message || 'Unknown error'));
    }
  }

  // --- Actionable Brain Chat handler methods ---

  _getActionItems(btn) {
    try {
      var action = JSON.parse(decodeURIComponent(btn.dataset.payload));
      var msgId = btn.closest('.action-block').dataset.msgId;
      var idx = btn.closest('.action-block').dataset.actionIdx;
      // Apply inline edits to items
      if (action.items) {
        for (var i = 0; i < action.items.length; i++) {
          var eKey = msgId + '-' + idx + '-' + i;
          if (this.actionEdits[eKey]) {
            action.items[i].content = this.actionEdits[eKey];
          }
        }
      }
      return { action: action, msgId: msgId, idx: idx };
    } catch (e) {
      toast('error', 'Failed to parse action data');
      return null;
    }
  }

  _markActionDone(msgId, idx) {
    this.actionsDone[msgId + '-' + idx] = true;
    this.render();
  }

  async _handleActionSaveLibrary(btn) {
    var parsed = this._getActionItems(btn);
    if (!parsed) return;
    var items = parsed.action.items || [];
    if (items.length === 0) { toast('info', 'No items to save'); return; }
    try {
      btn.disabled = true;
      btn.textContent = 'Saving...';
      await client.saveToLibrary({ items: items.map(function(it) {
        return { platform: it.platform || 'linkedin', content: it.content, tone: it.tone || 'default', status: 'draft' };
      })});
      toast('success', items.length + ' post' + (items.length > 1 ? 's' : '') + ' saved to Library');
      this._markActionDone(parsed.msgId, parsed.idx);
    } catch (err) {
      toast('error', 'Save failed: ' + (err.message || 'Unknown error'));
      btn.disabled = false;
      btn.textContent = 'Save All to Library';
    }
  }

  async _handleActionScheduleWeek(btn) {
    var parsed = this._getActionItems(btn);
    if (!parsed) return;
    var items = parsed.action.items || [];
    if (items.length === 0) { toast('info', 'No items to schedule'); return; }
    try {
      btn.disabled = true;
      btn.textContent = 'Scheduling...';
      var now = new Date();
      var scheduled = items.map(function(it, i) {
        var d = new Date(now);
        d.setDate(d.getDate() + i + 1); // spread across next N days
        d.setHours(9, 0, 0, 0);
        return {
          platform: it.platform || 'linkedin',
          content: it.content,
          tone: it.tone || 'default',
          status: 'scheduled',
          scheduledAt: d.toISOString()
        };
      });
      await client.saveToLibrary({ items: scheduled });
      toast('success', items.length + ' post' + (items.length > 1 ? 's' : '') + ' scheduled for this week');
      this._markActionDone(parsed.msgId, parsed.idx);
    } catch (err) {
      toast('error', 'Schedule failed: ' + (err.message || 'Unknown error'));
      btn.disabled = false;
      btn.textContent = 'Schedule This Week';
    }
  }

  async _handleActionCreateCampaign(btn) {
    var parsed = this._getActionItems(btn);
    if (!parsed) return;
    var c = parsed.action.campaign || parsed.action;
    try {
      btn.disabled = true;
      btn.textContent = 'Creating...';
      await client.createCampaign({
        name: c.name || 'New Campaign',
        goal: c.goal || '',
        platforms: c.platforms || ['linkedin'],
        totalItems: c.totalItems || 10,
        durationDays: c.days || c.durationDays || 14,
        tone: c.tone || 'Professional',
        context: c.context || ''
      });
      toast('success', 'Campaign "' + (c.name || 'New Campaign') + '" created!');
      this._markActionDone(parsed.msgId, parsed.idx);
    } catch (err) {
      toast('error', 'Campaign creation failed: ' + (err.message || 'Unknown error'));
      btn.disabled = false;
      btn.textContent = 'Create This Campaign';
    }
  }

  async _handleActionGenerateTheme(btn) {
    try {
      var theme = JSON.parse(decodeURIComponent(btn.dataset.payload));
      btn.disabled = true;
      btn.textContent = 'Generating...';
      var res = await client.generateBatch({
        topic: theme.topic || theme.title,
        platforms: theme.platforms || ['linkedin'],
        count: 3
      });
      if (res.items && res.items.length > 0) {
        toast('success', res.items.length + ' posts generated from "' + (theme.title || 'theme') + '"');
      } else {
        toast('success', 'Posts generated from theme');
      }
      btn.textContent = 'Generated!';
      btn.style.opacity = '0.5';
    } catch (err) {
      toast('error', 'Generation failed: ' + (err.message || 'Unknown error'));
      btn.disabled = false;
      btn.textContent = 'Generate Posts';
    }
  }

  async _handleActionSaveInsight(btn) {
    var parsed = this._getActionItems(btn);
    if (!parsed) return;
    try {
      btn.disabled = true;
      btn.textContent = 'Saving...';
      await client.addFact({
        fact_type: parsed.action.fact_type || 'insight',
        content: parsed.action.content || ''
      });
      toast('success', 'Saved to Brain');
      this._markActionDone(parsed.msgId, parsed.idx);
    } catch (err) {
      toast('error', 'Save failed: ' + (err.message || 'Unknown error'));
      btn.disabled = false;
      btn.textContent = 'Save to Brain';
    }
  }

  async showSaveToBrainModal(content) {
    // 1. Pre-fetch Data
    let libraryItems = [];
    let campaigns = [];
    try {
      const [lib, camp] = await Promise.all([
        client.getContentLibrary({ limit: 50 }),
        client.getCampaigns()
      ]);
      libraryItems = lib.data || Array.isArray(lib) ? lib : [];
      campaigns = camp.data || Array.isArray(camp) ? camp : [];
    } catch (e) {
      console.warn("Target prefetch failed:", e);
    }

    const modal = document.createElement('div');
    modal.className = 'brain-chat-modal-overlay';
    modal.innerHTML = `
        <div class="brain-chat-modal" data-testid="brain-chat-save-modal" style="max-width: 500px;">
            <h3>Save to Brain Memory</h3>
            <div class="form-group">
                <label>Type</label>
                <select id="modal-fact-type" data-testid="brain-chat-save-type">
                    <option value="goal">Goal</option>
                    <option value="offer">Specific Offer</option>
                    <option value="rule_do">Writing Rule (Do)</option>
                    <option value="rule_dont">Writing Rule (Don't)</option>
                    <option value="general">General Truth</option>
                </select>
            </div>
            <div class="form-group">
                <label>Content</label>
                <textarea id="modal-fact-content" data-testid="brain-chat-save-content" rows="4"></textarea>
            </div>

            <div class="form-group">
                <label>Scope / Apply To</label>
                <select id="modal-fact-target-type">
                    <option value="brand">Global Brain</option>
                    <option value="content_item">Specific Library Post</option>
                    <option value="campaign">Specific Campaign</option>
                </select>
            </div>

            <div id="target-sub-selector" class="form-group hide">
                <!-- Target list injected here -->
            </div>

            <div class="form-group hide" id="update-now-wrap">
                <label style="display:flex; align-items:center; gap:8px; cursor:pointer; text-transform:none; font-weight:normal;">
                    <input type="checkbox" id="modal-update-now">
                    Update targeted content immediately with this new fact
                </label>
            </div>

            <div id="modal-progress" class="hide" style="font-size:12px; color:var(--primary); margin-bottom:12px; font-weight:600;"></div>

            <div class="brain-chat-modal-actions">
                <button class="btn btn-secondary" id="modal-cancel" data-testid="brain-chat-save-cancel">Cancel</button>
                <button class="btn btn-primary" id="modal-confirm" data-testid="brain-chat-save-confirm">Save Fact</button>
            </div>
        </div>
      `;
    document.body.appendChild(modal);

    const targetTypeEl = modal.querySelector('#modal-fact-target-type');
    const subSelectorEl = modal.querySelector('#target-sub-selector');
    const updateWrapEl = modal.querySelector('#update-now-wrap');
    const progressEl = modal.querySelector('#modal-progress');

    targetTypeEl.onchange = () => {
      const val = targetTypeEl.value;
      subSelectorEl.classList.add('hide');
      updateWrapEl.classList.add('hide');
      subSelectorEl.innerHTML = '';

      if (val === 'content_item') {
        subSelectorEl.classList.remove('hide');
        updateWrapEl.classList.remove('hide');
        subSelectorEl.innerHTML = `
                <label>Select Post</label>
                <select id="modal-target-id">
                    ${libraryItems.map(item => `<option value="${item.id}">${item.title || (item.content || '').substring(0, 30) + '...'}</option>`).join('')}
                </select>
            `;
      } else if (val === 'campaign') {
        subSelectorEl.classList.remove('hide');
        updateWrapEl.classList.remove('hide');
        subSelectorEl.innerHTML = `
                <label>Select Campaign</label>
                <select id="modal-target-id">
                    ${campaigns.map(c => `<option value="${c.id}">${c.name}</option>`).join('')}
                </select>
            `;
      }
    };

    // Set raw content
    modal.querySelector('#modal-fact-content').value = content;
    modal.querySelector('#modal-fact-content').focus();

    modal.querySelector('#modal-cancel').onclick = () => modal.remove();
    modal.querySelector('#modal-confirm').onclick = async () => {
      const type = modal.querySelector('#modal-fact-type').value;
      const fact = modal.querySelector('#modal-fact-content').value;
      const targetType = targetTypeEl.value;
      const targetId = modal.querySelector('#modal-target-id')?.value;
      const updateNow = modal.querySelector('#modal-update-now')?.checked;

      try {
        const btn = modal.querySelector('#modal-confirm');
        const cancelBtn = modal.querySelector('#modal-cancel');
        btn.disabled = true;
        cancelBtn.disabled = true;
        btn.textContent = "Saving...";

        // 1. Create Fact
        const addFactRes = await client.addFact({ fact_type: type, content: fact });
        const factId = addFactRes.id;

        // 2. Attach Targets if not brand
        if (targetType !== 'brand' && targetId) {
          btn.textContent = "Attaching...";
          await client.setFactTargets(factId, {
            targets: [{ type: targetType, targetId }]
          });
        }

        // 3. Update Now (Sequential)
        if (updateNow && targetId) {
          if (targetType === 'content_item') {
            progressEl.classList.remove('hide');
            progressEl.textContent = "Updating content with Brain...";
            await client.rewriteContentItem(targetId);
          } else if (targetType === 'campaign') {
            progressEl.classList.remove('hide');
            progressEl.textContent = "Updating entire campaign with Brain...";
            await client.rewriteCampaign(targetId);
          }
        }

        this.showToast("Saved");
        modal.remove();
        await this.refresh();
      } catch (err) {
        toast('error', "Failed to save: " + err.message);
        modal.querySelector('#modal-confirm').disabled = false;
        modal.querySelector('#modal-cancel').disabled = false;
        modal.querySelector('#modal-confirm').textContent = "Save Fact";
        progressEl.classList.add('hide');
      }
    };
  }

  async runChatSearch(q) {
    this.chatSearchQ = q.trim();
    if (this.chatSearchQ.length < 2) {
      this.chatSearchResults = [];
      this.render();
      return;
    }

    try {
      this.chatSearchLoading = true;
      this.render();
      const res = await client.chatSearch({ q: this.chatSearchQ, projectId: this.activeProjectId });
      this.chatSearchResults = res.data || [];
    } catch (err) {
      console.error("[SEARCH_ERROR]", err);
      toast('error', 'Search failed — please try again.');
    } finally {
      this.chatSearchLoading = false;
      this.render();
    }
  }

  async handleSearchResultJump(threadId, messageId) {
    // 1) Switch thread
    await this.handleSelectThread(threadId);

    // 2) Scroll to message
    setTimeout(() => {
      const el = this.container.querySelector(`.chat-message[data-id="${messageId}"]`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });

        // 3) Highlight
        this.highlightMessageId = messageId;
        this.render();

        setTimeout(() => {
          this.highlightMessageId = null;
          this.render();
        }, 1500);
      }
    }, 100);
  }

  async unmount() {
    this._eventsBound = false;
  }

  // Helper from previous handleFileUpload / handleDeleteDoc
  showVaultError(msg) {
    const banner = this.container.querySelector("#vault-error-banner");
    if (banner) {
      banner.textContent = msg;
      banner.classList.remove("hide");
      setTimeout(() => banner.classList.add("hide"), 3000);
    } else {
      toast('error', msg);
    }
  }

  async handleFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      await this.handleFileUpload(file);
    }
  }

  async handleFileUpload(file) {
    const uploadBtn = this.container.querySelector("#brain-vault-upload-btn");
    const zone = this.container.querySelector("#vault-upload-zone");

    try {
      uploadBtn.disabled = true;
      uploadBtn.textContent = "Uploading...";
      zone.classList.add("uploading");

      const formData = new FormData();
      formData.append("file", file);

      await client.uploadDocument(formData);
      await this.refresh();
    } catch (err) {
      this.showVaultError("Upload failed: " + err.message);
    } finally {
      if (uploadBtn) {
        uploadBtn.disabled = false;
        uploadBtn.textContent = "Select File";
      }
      zone?.classList.remove("uploading");
    }
  }

  async handleDeleteDoc(id) {
    if (!await confirmModal({ title: 'Delete Document', message: 'Delete this document from vault?', confirmLabel: 'Delete', danger: true })) return;
    try {
      await client.deleteDocument(id);
      await this.refresh();
    } catch (err) {
      this.showVaultError("Delete failed: " + err.message);
    }
  }

  async handleSaveProfile() {
    const saveBtn = this.container.querySelector("#save-profile");
    let statusEl = this.container.querySelector("#save-status");

    const payload = {
      mission: this.container.querySelector("#profile-mission").value,
      audience: this.container.querySelector("#profile-audience").value,
      voice: this.container.querySelector("#profile-voice").value,
      offers_summary: this.container.querySelector("#profile-offers").value,
      cta: this.container.querySelector("#profile-cta").value,
      keywords: this.container.querySelector("#profile-keywords").value,
      avoid_words: this.container.querySelector("#profile-avoid").value
    };

    try {
      saveBtn.disabled = true;
      await client.updateProfile(payload);
      await this.refresh();
      statusEl = this.container.querySelector("#save-status");
      if (statusEl) {
        statusEl.classList.remove("hide");
        setTimeout(() => statusEl.classList.add("hide"), 2000);
      }
    } catch (err) {
      toast('error', "Failed to save: " + err.message);
    } finally {
      saveBtn.disabled = false;
    }
  }

  async handleAddFact() {
    const contentEl = this.container.querySelector("#new-fact-content");
    const typeEl = this.container.querySelector("#fact-type");
    const addBtn = this.container.querySelector("#add-fact");

    if (!contentEl.value) return;

    try {
      addBtn.disabled = true;
      await client.addFact({ fact: contentEl.value, type: typeEl.value });
      contentEl.value = "";
      await this.refresh();
    } catch (err) {
      toast('error', "Failed to add fact: " + err.message);
    } finally {
      if (addBtn) addBtn.disabled = false;
    }
  }

  async handleDeleteFact(id) {
    if (!await confirmModal({ title: 'Remove Fact', message: 'Remove this fact from memory?', confirmLabel: 'Remove', danger: true })) return;
    try {
      await client.deleteFact(id);
      await this.refresh();
    } catch (err) {
      toast('error', "Failed to delete: " + err.message);
    }
  }

  // --- Phase Q.4C Handlers ---
  async handleExport(type, format) {
    try {
      const id = type === 'thread' ? this.activeThreadId : this.activeProjectId;
      if (!id) return;

      const response = type === 'thread'
        ? await client.exportChatThread(id, format)
        : await client.exportChatProject(id, format);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${id}.${format === 'json' ? 'json' : 'md'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error("[EXPORT_ERROR]", err);
      toast('error', "Export failed: " + err.message);
    }
  }

  async handleShare() {
    try {
      if (!this.activeThreadId) return;
      const res = await client.shareChatThread(this.activeThreadId);
      this.activeShare = res.data;
      this.shareModalOpen = true;
      this.render();
    } catch (err) {
      console.error("[SHARE_ERROR]", err);
      toast('error', "Share failed: " + (err.error?.message || err.message));
    }
  }

  async handleRevokeShare(shareId) {
    if (!await confirmModal({ title: 'Revoke Share', message: 'Revoke this share link? It will no longer be accessible.', confirmLabel: 'Revoke', danger: true })) return;
    try {
      await client.revokeChatShare(shareId);
      this.shareModalOpen = false;
      this.activeShare = null;
      this.render();
      this.showToast("Share revoked.");
    } catch (err) {
      console.error("[REVOKE_SHARE_ERROR]", err);
      toast('error', "Revoke failed: " + err.message);
    }
  }

  renderShareModal() {
    if (!this.activeShare) return '';
    const shareUrl = this.activeShare.url;
    return `
      <div class="brain-chat-modal-overlay">
        <div class="brain-chat-modal">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px;">
            <h3 style="margin:0;">Share Thread</h3>
            <button id="close-share-modal" style="background:none; border:none; font-size:24px; color:var(--text-muted); cursor:pointer;">&times;</button>
          </div>
          <p style="font-size:14px; color:var(--text-muted); margin-bottom:16px;">
            Anyone with this link can view a read-only snapshot of this conversation.
          </p>
          <div class="share-link-box">
            <div class="share-link-url" title="${window.location.origin}${shareUrl}">${window.location.origin}${shareUrl}</div>
            <button class="copy-btn" data-url="${shareUrl}">Copy</button>
          </div>
          <div style="font-size:12px; color:var(--text-muted); margin-top:16px;">
            Expires: ${new Date(this.activeShare.expires_at).toLocaleDateString()}
          </div>
          <div style="margin-top:24px; text-align:right;">
            <button class="chat-actions-item danger" data-action="revoke-share" data-id="${this.activeShare.shareId}" style="width:auto; display:inline-flex; border: 1px solid var(--danger-bg); border-radius: 6px;">
              Revoke Access
            </button>
          </div>
        </div>
      </div>
    `;
  }
}
